import { Fragment, createContext, forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    message,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
    Upload,
} from 'antd';
import {
    CalendarOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SolutionOutlined,
    TagsOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import TipTapEditor from './TipTapEditor';
import DocumentViewer from './DocumentViewer';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchJsonWithAuth, getActingIdentity } from '../services/authApi';
import { autoValueFor } from '../utils/autoValueRules';
import { AUTH_URL } from '../services/apiConfig';
import { getDropdownValues, searchModuleData } from '../services/moduleDataApi';
import { getFormGroups, createModuleRecord } from '../services/addFormApi';
import { verifyEmail, getModuleDefaultFields } from '../services/adminApi';
import { uploadModuleDocuments } from '../services/documentApi';
import { getAdminClients, getCustomForms, getRoleFormPermissions } from '../services/adminApi';
import { updateRecord } from '../services/detailedViewApi';
import { loadDetailDefaults, formatPhone, phoneDigits, parsePhoneFormat } from '../services/detailDefaults';
import { groupAllowedByRole, fieldAllowedByRole, fieldEditableByRole } from '../utils/roleFormPermissionGate';
import { buildPayload, buildInitialValue, buildRepeatFieldInitial, singleFieldOf, collectFileParts, effectiveDataType, getDeep, toSelectControlValue, usesLabeledSelectValue, rowDefaultsFor } from './form/payloadTransformer';
import { computeLinkedSets, findLinkedSet, padLinkedInitialValues } from './form/linkedAddRowGroups';
import useAiActions, { groupActionsByPosition } from './form/useAiActions';
import AiActionButtonGroup from './form/AiActionButtons';
import { applyInputValidator, getKeyPattern } from './form/inputValidator';
import { configuredEmailRule, isValidConfiguredEmail } from './form/emailValidator';
import { securityValidationRule } from './form/inputSecurity';
import {
    buildUniqueValidationRule,
    attachUniqueSyncGuards,
    collectUniqueRules,
    createUniqueChecker,
    extractDuplicateFieldErrors,
} from './form/uniqueFieldValidation';
import { checkFieldUnique } from '../services/uniqueValidationApi';
import { uploadAcceptCategory } from './form/uploadAccept';
import { useQuickField } from './form/QuickCreateEditField';
import { maxFromFieldRule, maxFromFieldDependency, resolveScopedFieldPath } from './form/crossFieldRules';
import { checkMaxWithFallback, parseCompareFields, resolveCompareFieldName } from './form/maxCeiling';
import { contextRecordId } from './form/contextPrefill';
import { prefillWhenWatchFields, prefillWhenWatchPaths, resolvePrefillWhen } from './form/prefillWhenRules';
import { narrowOptionsByConstraint, stripExclusivePrefillOptions } from './form/optionConstraints';
import { filterLookupOptionRows, lookupExtraFieldKeys } from './form/optionRowFilters';
import { fieldTooltipText } from './form/fieldTooltip';
import { writeFormValue } from './form/applyGroupValues';
import { roleAllowsAction } from '../utils/roleAllowsAction';
import { resolveAfterSubmit, safeNavTarget } from '../utils/afterSubmitNav';
import { goBackOrTo } from '../utils/backNav';
import useDuplicateCheck from './form/useDuplicateCheck';
import {
    resolveRegionRules,
    inProgressAllowed,
    inProgressBlocks,
    inProgressError,
    buildInProgressValidator,
    promptRowMove,
} from './form/educationRules';
import { getDefaults } from '../services/detailDefaults';
import { openFormNotice, openFormDecision } from './form/formDecisionDialog';
import { applyGroupClear } from './form/clearGroupOnChange';
import { storedFileName, storedFileUrl } from './form/storedFileName';
import { fetchLookupLabels, mergeResolvedOptions, missingOptionValues } from './form/resolveStoredOptions';
import {
    buildPairValidator,
    buildRowHierarchyValidator,
    buildDisabledDate,
    defaultPickerValue,
    getComparableDateTime,
    getComparableTimeOfDay,
} from './form/dateRules';
import { submitSuccessMessage, submitErrorMessage } from '../utils/submitMessages';
import { scrollToFirstFormError } from '../utils/scrollToFirstFormError';
import { conditionFieldPath, configuredFieldLabel, conditionMatches as labelConditionMatches, colWhenRules, resolveColSpan } from './form/conditionalFieldLabel';
import AppBreadcrumb from '../components/Breadcrumb';
import ListView from './ListView';
import { clearAuthTokenOverride, setAuthTokenOverride } from '../services/authApi';
import { takePendingPromise } from '../services/pendingHandoff';
import { matchRelevantSkills } from '../services/skillMatchApi';
import {
    buildSourceTabs,
    getAvailableSourceActions,
    getSearchSources,
    getSourceActionInstanceKey,
    resolveSearchSource,
    runSourceAction,
    sourceActionAppliesToRecord,
} from '../services/sourceProviderActions';

const { Text } = Typography;
const { TextArea } = Input;
const emptyGroups = [];

const sectionIcons = [
    <UserOutlined />,
    <EnvironmentOutlined />,
    <SolutionOutlined />,
    <InfoCircleOutlined />,
    <FileTextOutlined />,
];

const fieldIcons = {
    email: <MailOutlined className="v1-input-icon" />,
    contactNumber: <PhoneOutlined className="v1-input-icon" />,
    phone: <PhoneOutlined className="v1-input-icon" />,
    currentLocation: <EnvironmentOutlined className="v1-input-icon" />,
    preferredLocation: <EnvironmentOutlined className="v1-input-icon" />,
    skills: <TagsOutlined className="v1-input-icon" />,
    firstName: <UserOutlined className="v1-input-icon" />,
    lastName: <UserOutlined className="v1-input-icon" />,
};

const truthy = (value) => value === true || value === 1 || value === '1';
const editable = (field, moduleName) => (field?.edit === undefined || truthy(field.edit))
    && (!field?.editablePermission || roleAllowsAction(field.editablePermission, moduleName));

function normalizeOptions(options = []) {
    return options.map((option) => {
        if (typeof option === 'string') return { label: option, value: option };
        return {
            label: option.label ?? option.name ?? option.value,
            value: option.value ?? option.id ?? option.name ?? option.label,
        };
    });
}

function namePathFromString(path) {
    return String(path ?? '').split('.').map((part) => part.trim()).filter(Boolean);
}

function siblingFieldPath(absoluteName, key) {
    return Array.isArray(absoluteName) && absoluteName.length > 1
        ? [...absoluteName.slice(0, -1), key]
        : [key];
}

// VerifyFieldButton — admin-configured "Verify" button beside a field
// (field.verifyAction, e.g. "email"). Calls the generic /email-verify
// endpoint and writes the boolean result + a timestamp into two sibling
// fields (verifyResultField/verifyTimestampField), within the same addRow
// row when nested. Not specific to "recruiter" or any module — any field
// can opt in.
function VerifyFieldButton({ form, field, absoluteName }) {
    const [checking, setChecking] = useState(false);
    const [verdict, setVerdict] = useState(null);
    const verifiedValueRef = useRef(null);
    const resultValue = Form.useWatch(
        field.verifyResultField ? siblingFieldPath(absoluteName, field.verifyResultField) : ['__noop_verify__'],
        { form, preserve: true },
    );
    const currentValue = Form.useWatch(absoluteName, form);

    // A successful result belongs to the exact value that was checked. Existing
    // Edit records hydrate once; later source-value changes invalidate the
    // configured result so the form gate cannot accept a different email.
    useEffect(() => {
        const normalized = String(currentValue ?? '').trim();
        if (resultValue === true && verifiedValueRef.current === null && normalized) {
            verifiedValueRef.current = normalized;
            return;
        }
        if (resultValue === true && verifiedValueRef.current !== null && normalized !== verifiedValueRef.current) {
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), false);
            if (field.verifyTimestampField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), undefined);
            verifiedValueRef.current = null;
        }
    }, [absoluteName, currentValue, field.verifyResultField, field.verifyTimestampField, form, resultValue]);

    if (field.verifyAction !== 'email') return null;

    const runVerify = async () => {
        const email = String(currentValue ?? '').trim();
        if (!email) {
            message.warning('Enter an email first');
            return;
        }
        setChecking(true);
        try {
            const result = await verifyEmail(email);
            verifiedValueRef.current = result.valid ? email : null;
            setVerdict(result.valid ? result : null);
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), result.valid);
            if (field.verifyTimestampField) {
                form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), result.valid ? new Date().toISOString() : undefined);
            }
            // Three outcomes, not two. "Accepted but unconfirmed" is the common
            // case on catch-all domains (Google Workspace's default, and most
            // corporate mail) — reporting it as a plain success would tell the
            // user the mailbox exists when nothing proved that.
            if (!result.valid) {
                message.error(result.reason ? `Invalid email: ${result.reason}` : 'Email could not be verified');
            } else if (result.mailboxConfirmed) {
                message.success('Email verified \u2014 the mail server confirmed this mailbox');
            } else {
                message.warning(result.reason || 'The domain can receive mail, but this exact mailbox could not be confirmed');
            }
        } catch (err) {
            verifiedValueRef.current = null;
            setVerdict(null);
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), false);
            if (field.verifyTimestampField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), undefined);
            message.error(err?.message || 'Could not verify email');
        } finally {
            setChecking(false);
        }
    };

    if (resultValue === true) {
        // Accepted, but the mailbox itself was never proven (catch-all domain).
        // Labelling that "Valid" would overstate what the check established, so
        // it gets its own state. The address still passes the submit gate — the
        // account email that follows is what ultimately proves delivery.
        // Only positive evidence of "unconfirmed" downgrades the label. A
        // response that simply doesn't carry the field (older backend, or a
        // check that never reached the mailbox stage) keeps the plain success
        // state rather than casting doubt the check never actually raised.
        if (verdict && !verdict.mailboxConfirmed && (verdict.catchAll || verdict.result === 'risky')) {
            return (
                <Tooltip title={verdict.reason || 'This domain accepts every address, so the mailbox could not be confirmed. Delivery is proven only once the account email is actually sent.'}>
                    <Text type="warning" strong className="v1-verify-field-wrap">
                        {field.verifyUnconfirmedLabel || 'Accepted'}
                    </Text>
                </Tooltip>
            );
        }
        return <Text type="success" strong className="v1-verify-field-wrap">{field.verifySuccessLabel || 'Valid'}</Text>;
    }

    return (
        <Button size="small" loading={checking} onClick={runVerify} className="v1-verify-field-wrap">
            {resultValue === false ? (field.verifyRetryLabel || 'Re Verify') : (field.verifyIdleLabel || 'Verify Email')}
        </Button>
    );
}

function ConfiguredFieldLabel({ form, field, siblingPrefix, locked, combined = false }) {
    const watchedValue = Form.useWatch(conditionFieldPath(field?.labelWhen, siblingPrefix), form);
    const label = configuredFieldLabel(field, watchedValue, combined);
    return combined ? label : <FieldLabel field={field} label={label} locked={locked} />;
}

// An ADDITIONAL admin-configured submit button (group.formActions). Same
// validation gate as the primary Create button, plus an optional enabledWhen
// gate that keeps it disabled until a field satisfies the condition (e.g. only
// once "Assign To" is filled). `tone` picks primary vs secondary styling.
function FormActionButton({ form, groups, action, disabled, loading, onFire }) {
    const unvalidated = Form.useWatch(
        (values) => groups.some((group) => {
            if (!group.addRow || !group.requireValidatedField) return false;
            const rows = values?.[group.name] ?? [];
            return rows.length === 0 || rows.some((row) => !row?.[group.requireValidatedField]);
        }),
        { form, preserve: true },
    );
    const gateValue = Form.useWatch(action?.enabledWhen?.field, form);
    const gateOk = !action?.enabledWhen?.field || groupShowIfMatches(action.enabledWhen, gateValue);
    return (
        <Button
            type={action?.tone === 'primary' ? 'primary' : 'default'}
            onClick={onFire}
            disabled={disabled || unvalidated || !gateOk}
            loading={loading}
        >
            {action?.label || 'Submit'}
        </Button>
    );
}

function ValidationGatedSubmitButton({ form, groups, disabled, loading, onClick, children }) {
    const unvalidated = Form.useWatch(
        (values) => groups.some((group) => {
            if (!group.addRow || !group.requireValidatedField) return false;
            const rows = values?.[group.name] ?? [];
            return rows.length === 0 || rows.some((row) => !row?.[group.requireValidatedField]);
        }),
        { form, preserve: true },
    );
    const isDisabled = disabled || unvalidated;
    // Config-driven "why is this disabled" tooltip: an admin sets
    // group.submitDisabledMessage (Admin → Form Groups). When a row-validation
    // gate is what's blocking, the more specific requireValidatedFieldMessage
    // (or a sensible default) is shown instead. Only rendered while disabled.
    const groupMsg = groups.find((g) => g.submitDisabledMessage)?.submitDisabledMessage;
    const validatedMsg = groups.find((g) => g.requireValidatedField)?.requireValidatedFieldMessage;
    const reason = unvalidated
        ? (validatedMsg || groupMsg || 'Verify every required row before submitting.')
        : (groupMsg || 'Complete all mandatory fields before submitting.');
    const button = (
        <Button type="primary" onClick={onClick} disabled={isDisabled} loading={loading}>
            {children}
        </Button>
    );
    // A disabled antd Button has pointer-events:none, so the Tooltip must wrap a
    // span to receive hover.
    return isDisabled && !loading
        ? <Tooltip title={reason}><span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{button}</span></Tooltip>
        : button;
}

// PrefillWhenWatcher — sets ITS OWN field's value when a referenced field's
// value matches a configured rule (e.g. contractType="W2" forces employerId to
// a fixed record id), and clears it back out when a rule says so or when no
// rule matches any more. Opposite direction from autofillFrom (which pushes
// into siblings when the changed field is a lookup select) — this field PULLS
// its value from a field changing elsewhere. A tiny standalone component (not
// inline logic) because Form.useWatch is a hook and must run in component
// scope, not inside the plain renderField() function.
//
// ALL of the decision-making lives in form/prefillWhenRules.js so this form and
// EditFormV1 can never disagree about what a rule means, and so the rules can
// be unit-tested without importing either form (any render test that touches
// them dies on jsdom's missing DOMMatrix, via react-pdf).
//
// `watchPaths` carries EVERY field a rule references — rules may point at
// different fields, and each path was already resolved through renderField's
// `prefixFor` closure so a rule reaching out of an addRow row lands on the
// top-level field (see prefillWhenWatchPaths).
function PrefillWhenWatcher({ form, rules, watchPaths, targetPath, resetOnNoMatch = true }) {
    // A selector useWatch (not one path per hook — hook count must be stable)
    // reduced to a STRING so the effect dependency stays a primitive; a fresh
    // array every render would re-run the effect on every keystroke anywhere.
    const watchedKey = Form.useWatch(
        (values) => JSON.stringify(
            watchPaths.map(({ path }) => path.reduce((acc, key) => (acc == null ? acc : acc[key]), values) ?? null),
        ),
        { form, preserve: true },
    );
    // Tracks the value WE last set via a matching rule (e.g. the W2 default
    // employer), so a later "no rule matches" tick (contractType switched away
    // from W2) can tell "still exactly what we set" apart from "the user picked
    // something else since" — only the former gets cleared. Without this guard,
    // switching e.g. W2 → C2C left the W2 default employer stuck in the field
    // forever (admin config `prefillWhenReset`, default on).
    const lastAppliedRef = useRef(undefined);
    // First tick = the form settling into its initial values, never a user
    // decision, so a clear rule must not fire on it (it would delete stored
    // data on the edit form). See prefillWhenRules.js for the full rationale.
    const initialRef = useRef(true);
    useEffect(() => {
        const values = watchedKey ? JSON.parse(watchedKey) : [];
        const watched = {};
        watchPaths.forEach(({ field }, i) => { watched[field] = values[i]; });
        const initial = initialRef.current;
        initialRef.current = false;
        const outcome = resolvePrefillWhen({
            rules,
            watched,
            current: form.getFieldValue(targetPath),
            lastApplied: lastAppliedRef.current,
            clearOnMismatch: resetOnNoMatch,
            initial,
        });
        if (outcome.action === 'set') {
            form.setFieldValue(targetPath, outcome.value);
            lastAppliedRef.current = outcome.value;
        } else if (outcome.action === 'clear') {
            form.setFieldValue(targetPath, undefined);
            lastAppliedRef.current = undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedKey]);
    return null;
}

// AutoFillFromApiWatcher — watches THIS field's own live value and, when it
// changes to a non-empty value, calls a REST endpoint and populates one or
// more sibling fields from the JSON response. Unlike prefillWhen (a static
// value → value map), this does a real API round-trip — e.g. auto-loading a
// campaign's Subject/Message from an admin-configured email template the
// moment Type is set. A failed/404 lookup (no template configured yet) is
// treated as a silent no-op, matching how callers of this endpoint already
// tolerate a missing template elsewhere.
function AutoFillFromApiWatcher({ form, config, watchPath }) {
    const watched = Form.useWatch(watchPath, form);
    const lastFetchedRef = useRef(undefined);
    useEffect(() => {
        if (empty(watched) || watched === lastFetchedRef.current) return;
        lastFetchedRef.current = watched;

        const url = config.endpointTemplate
            ? `${config.endpointTemplate.replace('{value}', encodeURIComponent(String(watched)))}`
            : `${config.endpoint}?${new URLSearchParams({ [config.queryParam]: String(watched) }).toString()}`;

        fetchJsonWithAuth(AUTH_URL, url)
            .then((json) => {
                const data = json?.data ?? json;
                if (config.substituteTokens) {
                    (config.substituteInFields || []).forEach((targetField) => {
                        const targetPath = namePathFromString(targetField);
                        let current = form.getFieldValue(targetPath) || '';
                        Object.entries(config.substituteTokens).forEach(([token, responseKey]) => {
                            const inner = token.replace(/^\{\{|\}\}$/g, '').trim();
                            const pattern = new RegExp(`\\{\\{\\s*${inner.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}\\}`, 'gi');
                            current = current.replace(pattern, data?.[responseKey] ?? '');
                        });
                        form.setFieldValue(targetPath, current);
                    });
                    return;
                }
                Object.entries(config.mapResponseToFields).forEach(([targetField, responseKey]) => {
                    const targetPath = namePathFromString(targetField);
                    if (config.onlyIfEmpty && !empty(form.getFieldValue(targetPath))) return;
                    form.setFieldValue(targetPath, data?.[responseKey]);
                });
            })
            .catch(() => {
                // No template configured for this value yet, or the call
                // failed — leave existing field values untouched.
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watched]);
    return null;
}
// Group-level showIf: the whole group renders only when the referenced field's
// live value satisfies the condition (same operators as field-level showIf).
// Kept as a module-level helper so both AddFormV1 and EditFormV1 evaluate it
// identically.
function groupShowIfMatches(showIf, value) {
    if (!showIf?.field) return true;
    const actual = String(value ?? '');
    const expected = String(showIf.value ?? '');
    const list = () => expected.split(',').map((v) => v.trim());
    switch (showIf.operator) {
        case 'eq': return actual === expected;
        case 'neq': return actual !== expected;
        case 'in': return list().includes(actual);
        case 'notIn': return !list().includes(actual);
        case 'truthy': return truthy(value);
        case 'falsy': return !truthy(value);
        case 'empty':
        case 'isEmpty':
            return value === undefined || value === null || actual.trim() === '' || (Array.isArray(value) && value.length === 0);
        case 'notEmpty':
            return !(value === undefined || value === null || actual.trim() === '' || (Array.isArray(value) && value.length === 0));
        default: return true;
    }
}

// A group with a `showIf` renders through this gate so its visibility reacts to
// the referenced field changing. `render` is only invoked when visible, so a
// hidden group's fields never register (nothing to validate or submit) — exactly
// like a hidden field-level showIf. Groups without showIf bypass this entirely.
function GroupShowIfGate({ form, showIf, render }) {
    const watched = Form.useWatch(showIf.field, form);
    if (!groupShowIfMatches(showIf, watched)) return null;
    return render();
}

// Group-level disable: the whole card (every field in it) goes read-only when
// the group's `disabled` flag is set OR its `disabledIf` condition matches — the
// group counterpart of FieldControl's field-level disabled/disabledIf. Provided
// through context so each FieldControl in the group ORs it into its own disabled
// state without threading a prop through renderGroup→renderFields→renderField.
// Nested/merged groups inherit the parent's disabled state (never re-enable).
const GroupDisabledContext = createContext(false);

function GroupDisabledProvider({ form, group, children, inheritParent = true }) {
    const parentDisabled = useContext(GroupDisabledContext);
    const path = group?.disabledIf?.field ? namePathFromString(group.disabledIf.field) : ['__noop_group_disabled__'];
    const watched = Form.useWatch(path, form);
    const conditionDisabled = group?.disabledIf?.field
        ? groupShowIfMatches(group.disabledIf, watched)
        : false;
    // `inheritParent: false` is used for a MERGED child (group.mergeInto).
    // mergeInto is presentation only — the child borrows the host's card and
    // nothing else — so inheriting the host's disabled state would give a
    // display relationship real behaviour.
    //
    // It is also the difference between working and deadlocked: the Employer
    // section is disabled until a Contract Type is chosen, and Contract Type is
    // rendered INSIDE that section. Inheriting would disable the very control
    // that enables the card, with no way out.
    const disabled = (inheritParent && parentDisabled)
        || Boolean(group?.disabled)
        || conditionDisabled;
    return <GroupDisabledContext.Provider value={disabled}>{children}</GroupDisabledContext.Provider>;
}

function getNestedProp(obj, dotPath) {
    return String(dotPath ?? '').split('.').reduce((cur, k) => cur?.[k], obj);
}

function hasMeaningfulValue(value) {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0 && value.some(hasMeaningfulValue);
    if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
    return true;
}

function extractGroups(formGroups) {
    if (Array.isArray(formGroups)) return formGroups;
    if (Array.isArray(formGroups?.groups)) return formGroups.groups;
    if (Array.isArray(formGroups?.data)) return formGroups.data;
    if (Array.isArray(formGroups?.data?.groups)) return formGroups.data.groups;
    return emptyGroups;
}

function normalizeGroups(formGroups = [], moduleName, rolePerms, defaultFieldSet) {
    const dset = defaultFieldSet ?? new Set();
    return [...extractGroups(formGroups)]
        // hideGroup hides everywhere; hideGroupInForm is the form-only mirror of
        // DetailViewV1's hideGroupInDetail — for server-owned groups that must be
        // read on the detail view but never typed here.
        .filter((group) => !truthy(group.hideGroup) && !truthy(group.hideGroupInForm)
            && groupAllowedByRole(rolePerms, group.name))
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
        .map((group) => ({
            ...group,
            fields: [...(group.fields ?? [])]
                // A "default field" (Form Groups → Default Fields, OTP-gated) is
                // ALWAYS shown + required regardless of its own show flag, so it
                // survives the hidden-field filter below and is forced show/req in
                // the map that follows.
                .map((field) => (
                    dset.has(field.field) ? { ...field, show: 1, req: 1 } : field
                ))
                // Drop blank-keyed fields (a stray admin-config row can't render or
                // submit), any client-scope "gate" field (rendered as the top
                // "Select Client" picker, never as a body field), any field whose
                // visiblePermission the current role doesn't hold, and any field
                // this role's Form Configuration (Role Configure → Form, derived
                // from this same Form Groups config) has turned off.
                //
                // A field with show:0 ("hidden") is intentionally KEPT here even
                // though it must never render — this same fields array also feeds
                // buildPayload/buildRowObject (the payload engine has no other way
                // to know a hidden carrier field like isValid/isAccountCreate
                // exists). Dropping it here silently threw away every hidden
                // field's value at submit time, even one just set moments earlier
                // in this session (e.g. a Verify Email click stamping isValid on a
                // brand-new recruiter row) — see renderFields, which is what
                // actually keeps hidden fields off the screen.
                .filter((field) =>
                    String(field.field ?? '').trim() !== ''
                    && field.showOnAdd !== false
                    && !truthy(field.clientScope)
                    && (!field.visiblePermission || roleAllowsAction(field.visiblePermission, moduleName))
                    && fieldAllowedByRole(rolePerms, group.name, field.field))
                // Role "disable": a group/field the role has marked non-editable
                // still renders but read-only. FieldControl honours field.disabled.
                .map((field) => (
                    fieldEditableByRole(rolePerms, group.name, field.field)
                        ? field
                        : { ...field, disabled: true }
                ))
                .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
        }))
        .filter((group) => group.fields.length);
}

// pickPrefillValue reads a field's initial value from a prefill SOURCE record,
// honouring the admin-config `prefillFrom` mapping FIRST (so a target field can
// inherit from a differently-named source key — e.g. submission.totalExperience
// ← candidate.yearOfExperience), then falling back to field.field and payloadKey.
// Tolerates both flat dotted keys (the getFormGroups flat record) and nested
// objects (addRow rows). Entirely config-driven — no field-name logic here.
// Flattens a form-groups response (scalar fields with embedded values) into a
// plain { fieldKey: value } record. Shared by the default prefill source and
// the cross-module context prefill (field.prefillFromModule).
function flatRecordFromGroups(groups) {
    if (!Array.isArray(groups)) return null;
    const rec = {};
    groups.forEach(group => {
        if (group.addRow) return;
        (group.fields ?? []).forEach(field => {
            if (!field.field || field.value === undefined || field.value === null) return;
            rec[field.field] = field.value;
            if (field.payloadKey && field.payloadKey !== field.field) {
                rec[field.payloadKey] = field.value;
            }
        });
    });
    return Object.keys(rec).length > 0 ? rec : null;
}

function pickPrefillValue(record, field) {
    if (!record || !field) return undefined;
    for (const path of [field.prefillFrom, field.field, field.payloadKey]) {
        if (!path) continue;
        let v = record[path];
        if (v === undefined || v === null) v = getDeep(record, path);
        if (v !== undefined && v !== null) return v;
    }
    return undefined;
}

// Convert a stored file-reference object to an Ant Design fileList entry so
// file fields pre-show previously uploaded files when prefilling. Handles:
//   resume:   { location, uploadedFileName, uniqueName }
//   passport: { passportCopyLocation, passportUploadName, … }
//   visa/alternateVisa: same *CopyLocation / *UploadName pattern
function toFileListValue(value) {
    // A multi-file field prefills from a stored ARRAY: map each element through
    // the same single-file logic so every entry keeps its `stored` original too
    // (it used to be handed to the control raw, which showed the files but lost
    // them on save).
    if (Array.isArray(value)) return value.flatMap((item) => toFileListValue(item));
    if (!value || typeof value !== 'object') return [];
    // Legacy schemas disagree about which key holds the original filename —
    // passport uses passportUploadName, visa uses visaUniqueName. Matching only
    // "*UploadName" found nothing for a visa, so it rendered as "file".
    // See form/storedFileName.js.
    const name = storedFileName(value);
    const url = storedFileUrl(value);
    if (!name && !url) return [];
    // `stored` keeps the ORIGINAL container exactly as the record holds it
    // (passportCopyLocation / passportUploadName / …). The payload engine writes
    // it straight back when the user leaves the prefilled file untouched, so a
    // Quick Submit from a previous submission carries its documents into the new
    // record without re-uploading and without guessing at the stored shape.
    return [{ uid: url || name || '-1', name: name || 'file', status: 'done', url, existing: true, stored: value }];
}

const empty = (value) => value === undefined || value === null || value === '';

const REGION_OPTIONS = [
    { label: 'US', value: 'US' },
    { label: 'UK', value: 'UK' },
    { label: 'India', value: 'INDIA' },
];

// Strip HTML tags and decode common entities — used so min/max validation
// checks visible character count, not raw markup length.
const stripHtml = (html) =>
    String(html)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();

const numberRangeRule = ({ label, message, value, compare, text }) => ({
    validator: (_, input) => {
        if (empty(input)) return Promise.resolve();
        return compare(Number(input), Number(value))
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${value}`));
    },
});

// Date comparison + picker-constraint helpers now live in ONE place
// (components/form/dateRules.js) and are imported at the top of this file.
// They used to be duplicated verbatim between AddFormV1 and EditFormV1, which
// is how a fix lands on Add and silently misses Edit.

// Same resolution both forms have always used, now shared (form/maxCeiling.js)
// so it can be unit tested: a DOTTED reference is absolute (reaches out of the
// validated field's container), a bare one is a sibling.
function getSiblingFieldName(currentName, compareField) {
    return resolveCompareFieldName(currentName, compareField);
}

const crossFieldNumberRule = ({ label, message, value, rule, form, name, compare, text }) => ({
    validator: async (_, input) => {
        const compareField = value ?? rule.compareField ?? rule.field;
        if (!compareField || empty(input)) return Promise.resolve();
        const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
        if (empty(compareValue)) return Promise.resolve();
        const inputNumber = Number(input);
        const compareNumber = Number(compareValue);
        if (Number.isNaN(inputNumber) || Number.isNaN(compareNumber)) return Promise.resolve();
        return compare(inputNumber, compareNumber)
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${compareField}`));
    },
});

const validationRulesByType = {
    noPastDate: ({ label, message }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const time = getComparableDateTime(value);
            if (time === null) return Promise.resolve();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return time < today.getTime()
                ? Promise.reject(new Error(message ?? `${label} cannot be a past date`))
                : Promise.resolve();
        },
    }),
    noFutureDate: ({ label, message }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const time = getComparableDateTime(value);
            if (time === null) return Promise.resolve();
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            return time > endOfDay.getTime()
                ? Promise.reject(new Error(message ?? `${label} cannot be a future date`))
                : Promise.resolve();
        },
    }),
    minAge: ({ label, message, value }) => ({
        validator: (_, input) => {
            if (empty(input)) return Promise.resolve();
            const time = getComparableDateTime(input);
            if (time === null) return Promise.resolve();
            const years = Number(value ?? 18);
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - years);
            cutoff.setHours(23, 59, 59, 999);
            return time > cutoff.getTime()
                ? Promise.reject(new Error(message ?? `Must be at least ${years} years old`))
                : Promise.resolve();
        },
    }),
    // Both date-pair rules delegate to the shared engine, so `strict` (reject an
    // equal date — the 7-July bug) and `minGap`/`minGapFrom` (the duration
    // dropdown) behave identically on Add and Edit, and the SAME rule object
    // also drives the picker's disabledDate below.
    dateAfterField: ({ label, message, value, rule, form, name }) => buildPairValidator({
        type: 'dateAfterField',
        label,
        rule: { ...rule, value: value ?? rule?.compareField ?? rule?.field, message },
        readSibling: (key) => form.getFieldValue(getSiblingFieldName(name, key)),
    }),
    dateBeforeField: ({ label, message, value, rule, form, name }) => buildPairValidator({
        type: 'dateBeforeField',
        label,
        rule: { ...rule, value: value ?? rule?.compareField ?? rule?.field, message },
        readSibling: (key) => form.getFieldValue(getSiblingFieldName(name, key)),
    }),
    timeAfterField: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime > compareTime) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must be after ${compareField}`));
        },
    }),
    timeBeforeField: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime < compareTime) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must be before ${compareField}`));
        },
    }),
    greaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input >= other, text: 'must be greater than or equal to' }),
    strictGreaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input > other, text: 'must be greater than' }),
    strictLessThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input < other, text: 'must be less than' }),
    lessThanOrEqualField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input <= other, text: 'must be less than or equal to' }),
    // maxFromField — { type:'maxFromField', field:'<otherKey>', message }. The
    // ONLY cross-field rule here that can reach OUT of its addRow row: it
    // resolves the referenced key through renderField's `prefixFor` closure,
    // i.e. exactly the scoping a Show Condition uses (row when the key is one of
    // the row's own fields, top level otherwise). That is what lets a per-row
    // "experience" be capped by a top-level overall-experience field, with which
    // field caps which left entirely to Form Groups config.
    maxFromField: (args) => maxFromFieldRule(args),
    // maxFieldWithFallback — cap against the FIRST compare field carrying a
    // usable maximum ("cap at the To value, else the From value"). Decision
    // logic (incl. why a 0 ceiling means "no ceiling") lives in form/maxCeiling.js.
    maxFieldWithFallback: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareFields = parseCompareFields(value ?? rule.compareFields);
            const result = checkMaxWithFallback({
                input,
                compareFields,
                readValue: (fieldName) => form.getFieldValue(getSiblingFieldName(name, fieldName)),
            });
            if (result.ok) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must not exceed ${result.field}`));
        },
    }),
    required: ({ field, message }) => ({
        validator: (_, value) => {
            let isEmpty = value === undefined || value === null || value === ''
                || (Array.isArray(value) && value.length === 0);
            if (!isEmpty && field?.type === 'text-editor') {
                isEmpty = stripHtml(value) === '';
            }
            return isEmpty
                ? Promise.reject(new Error(message ?? 'Mandatory Field'))
                : Promise.resolve();
        },
    }),
    email: ({ label, message }) => configuredEmailRule(label, message),
    // Admin-configured "already present" check. Everything about it — the
    // trigger, the blank handling, the normalizer, the message — comes from the
    // validations entry; the server resolves the collection and the scope.
    unique: ({ field, rule, uniqueContext }) => buildUniqueValidationRule({
        field, validation: rule, context: uniqueContext,
    }),

    emailTags: ({ label, message }) => ({
        validator: (_, value) => {
            const tags = Array.isArray(value) ? value : [];
            const invalid = tags.find((tag) => !isValidConfiguredEmail(tag));
            return invalid
                ? Promise.reject(new Error(message ?? `${label} contains an invalid email: ${invalid}`))
                : Promise.resolve();
        },
    }),
    url: ({ label, message }) => ({ type: 'url', message: message ?? `Enter valid ${label}` }),
    number: ({ label, message }) => ({ pattern: /^[0-9]+$/, message: message ?? `${label} must be a number` }),
    phone: ({ message }) => ({
        // Accepts the configured mask (e.g. 999-878-3413) — validates on the
        // stripped digits so the separators the formatter inserts don't fail it.
        validator: (_, value) => {
            if (value === undefined || value === null || value === '') return Promise.resolve();
            const { total } = parsePhoneFormat();
            return phoneDigits(value).length === total
                ? Promise.resolve()
                : Promise.reject(new Error(message ?? `Phone number must contain exactly ${total} digits`));
        },
    }),

    // The pre-existing minLength3 / minLength5 pair baked the threshold into the
    // TYPE NAME, and existed only in AddFormV1 — configuring one on a field did
    // nothing at all on the Edit form. Both now delegate to the generic `min`
    // rule (admin: "Min length/value"), which already implements exactly this
    // for non-number fields and is present in BOTH forms.
    minLength3: (args) => validationRulesByType.min({ ...args, value: 3 }),
    minLength5: (args) => validationRulesByType.min({ ...args, value: 5 }),

    len: ({ label, message, value }) => ({ len: Number(value), message: message ?? `${label} must be ${value} characters` }),
    pattern: ({ label, message, rule }) => ({
        pattern: new RegExp(rule.pattern ?? rule.value, rule.flags),
        message: message ?? `Enter valid ${label}`,
    }),
    min: ({ field, label, message, value }) => (
        field.type === 'number'
            ? numberRangeRule({ label, message, value, compare: (input, min) => input >= min, text: 'must be at least' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length >= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(message ?? `${label} must be at least ${value} characters`));
                },
            }
    ),
    max: ({ field, label, message, value }) => (
        field.type === 'number'
            ? numberRangeRule({ label, message, value, compare: (input, max) => input <= max, text: 'must not exceed' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length <= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(message ?? `${label} must not exceed ${value} characters`));
                },
            }
    ),
};

// `prefixFor` is renderField's showIf prefix resolver; it is forwarded so a rule
// that references another field resolves it with the same row-vs-top-level
// scoping the Show Conditions use. Undefined for call sites that have no row
// context (a repeat-scalar item), where the rules keep their sibling behaviour.
function validationToRule(validation, field, form, name, prefixFor, uniqueContext) {
    const rule = typeof validation === 'string' ? { type: validation } : validation;
    const type = rule.type ?? rule.rule ?? rule.name;
    const value = rule.value ?? rule.length ?? rule.min ?? rule.max;
    const label = field.label ?? field.field ?? 'Field';
    const factory = validationRulesByType[type];

    return factory
        ? factory({ field, label, message: rule.message, rule, value, form, name, prefixFor, uniqueContext })
        : rule;
}

function getSiblingFieldPath(name, siblingField) {
    return Array.isArray(name) && name.length > 1
        ? [...name.slice(0, -1), siblingField]
        : [siblingField];
}

// A field is required either via the simple "req" toggle or via a "required"
// entry in the admin-configured validations list — the Form.Item asterisk and
// the addRow "at least one row" check must honour both, not just field.req.
function isFieldRequired(field, form, name) {
    if (truthy(field?.req)) return true;
    const apiRules = field?.validations ?? field?.validation ?? field?.rules ?? [];
    if (apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required')) return true;
    if (field?.requiredIfField && form && name) {
        return truthy(form.getFieldValue(getSiblingFieldPath(name, field.requiredIfField)));
    }
    return false;
}

// `uniqueContext` ({ checker, module, clientId, region }) is what turns a
// configured `unique` validation into a live preflight check. Call sites with
// no context (search forms, repeat-scalar items) still render the field; the
// unique rule degrades to a no-op there and the server stays authoritative.
function getRules(field, form, name, skipRequired = false, prefixFor = undefined, uniqueContext = undefined,
    extraRules = undefined) {
    const label = field.label ?? field.field ?? 'Field';
    const apiRules = field.validations ?? field.validation ?? field.rules ?? [];
    const hasApiRequired = apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required');
    const hasApiEmail = apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'email');
    const rules = [];

    // Rules the CALLER resolved because they need the group, which this
    // function never sees — today, a region that refuses an in-progress entry.
    // They go first so a blocked row reports that before anything else.
    if (extraRules?.length) rules.push(...extraRules);

    // Cross-row hierarchy: a newest-first list constrains each row against its
    // NEIGHBOURS. The picker already greys the impossible dates out; this
    // catches one that arrived another way — typed, pasted, prefilled from a
    // résumé, or entered before the neighbouring row was filled in.
    if (field.rowHierarchy) {
        const ctx = rowContext(form, name);
        if (ctx.rows) rules.push(buildRowHierarchyValidator({ field, ...ctx }));
    }


    if (!skipRequired && truthy(field.req) && !hasApiRequired) {
        // A field disabled via disabledByField (e.g. endDate while
        // currentlyPursuing is checked) must not block submit with a
        // "required" error the user has no way to satisfy.
        if (field.disabledByField) {
            rules.push({
                validator: (_, input) => {
                    const disabledNow = truthy(form.getFieldValue(getSiblingFieldPath(name, field.disabledByField)));
                    if (disabledNow) return Promise.resolve();
                    return (input === undefined || input === null || input === '')
                        ? Promise.reject(new Error('Mandatory Field'))
                        : Promise.resolve();
                },
            });
        }

        if (!skipRequired && field.requiredIfField && !hasApiRequired) {
            rules.push({
                validator: (_, input) => {
                    const triggerValue = form.getFieldValue(getSiblingFieldPath(name, field.requiredIfField));
                    if (!truthy(triggerValue)) return Promise.resolve();
                    return (input === undefined || input === null || input === '')
                        ? Promise.reject(new Error('Mandatory Field'))
                        : Promise.resolve();
                },
            });
        }

        else {
            rules.push({ required: true, message: 'Mandatory Field' });
        }
    }
    if ((field.type === 'email' || field.formatter === 'email') && !hasApiEmail) {
        rules.push(configuredEmailRule(label));
    }
    // Number fields are whole-number-only unless the admin marks them decimal
    // (e.g. a commission of 5.5). Both patterns still reject any non-numeric
    // character, so "float only" stays enforced either way.
    if (field.type === 'number') {
        rules.push(truthy(field.decimal)
            ? { pattern: /^\d+(\.\d+)?$/, message: `${label} must be a number` }
            : { pattern: /^[0-9]+$/, message: `${label} must be a number` });
    }

    // attachUniqueSyncGuards re-wraps any `unique` rule so it only calls the
    // server once every synchronous rule in this same list has passed.
    return attachUniqueSyncGuards([
        ...rules,
        ...apiRules.map((rule) => validationToRule(rule, field, form, name, prefixFor, uniqueContext)),
        securityValidationRule(field),
    ]);
}

function getValidationValue(field, type) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const rule = rules.find((item) => (typeof item === 'string' ? item : item?.type) === type);
    if (!rule || typeof rule === 'string') return undefined;
    return rule.value ?? rule.length ?? rule[type];
}

function getInputLimits(field) {
    const len = field.len ?? getValidationValue(field, 'len');
    const min = field.min ?? getValidationValue(field, 'min');
    const max = field.max ?? getValidationValue(field, 'max');

    if (field.type === 'number') {
        return {
            min: min ?? undefined,
            max: max ?? undefined,
        };
    }

    return {
        minLength: min ?? undefined,
        maxLength: field.maxLength ?? len ?? max ?? undefined,
    };
}

// Thin adapters over the shared engine (components/form/dateRules.js). The
// bodies used to live here AND in EditFormV1; the picker's greyed-out range and
// the submit-time error are now generated from the same rule object, so a
// calendar that disagrees with its own error message is structurally impossible.
// rowContext — the sibling ROWS of the repeatable group this field sits in,
// plus this field's row index.
//
// A per-row `readSibling` can only see the row it is in, but a newest-first
// list constrains a row against its NEIGHBOURS. Inside a Form.List the field
// path is [listName, rowIndex, fieldKey], which is exactly what is needed to
// read the whole list and locate this row within it.
//
// Returns {} outside a repeatable group, so the hierarchy simply never applies.
function rowContext(form, name) {
    if (!Array.isArray(name) || name.length < 3 || typeof name[1] !== 'number') return {};
    const rows = form.getFieldValue(name[0]);
    if (!Array.isArray(rows)) return {};
    return { rows, rowIndex: name[1] };
}

function getDisabledDate(field, form, name) {
    return buildDisabledDate({
        field,
        readSibling: (key) => form.getFieldValue(getSiblingFieldName(name, key)),
        ...rowContext(form, name),
    });
}

function getDefaultPickerValue(field) {
    return defaultPickerValue(field);
}

function cleanValue(value, formatter) {
    if (!formatter) return value;
    // Phone gets the common, config-driven mask (default 3-3-4 → 999-878-3413).
    // "digits" stays a bare digit string (no separators).
    if (formatter === 'phone') return formatPhone(value);
    if (formatter === 'digits') return value.replace(/\D/g, '').slice(0, 10);
    if (formatter === 'email' || formatter === 'noSpaces') return value.replace(/\s/g, '');
    if (formatter === 'name') return value.replace(/\s+/g, ' ').trimStart().replace(/\b\w/g, (char) => char.toUpperCase());
    return value;
}

function normalizeModule(moduleName = '') {
    const normalized = String(moduleName).toLowerCase();
    if (normalized === 'job') return 'jobs';
    if (normalized === 'candidate') return 'candidates';
    if (normalized === 'submissions') return 'submission';
    return normalized;
}

const SEARCH_SOURCE_ONLY_FIELDS = new Set([
    'selectedSource',
    'source',
    'sourceType',
    'scope',
]);

function hasSearchCriterionValue(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'boolean') return value;
    if (dayjs.isDayjs(value)) return value.isValid();
    if (Array.isArray(value)) return value.some(hasSearchCriterionValue);
    if (typeof value === 'object') {
        if ('value' in value || 'label' in value || 'name' in value) {
            return hasSearchCriterionValue(value.value ?? value.label ?? value.name);
        }
        return Object.entries(value).some(([key, nestedValue]) => (
            !SEARCH_SOURCE_ONLY_FIELDS.has(key) && hasSearchCriterionValue(nestedValue)
        ));
    }

    return false;
}

function hasSearchCriteria(values = {}) {
    return Object.entries(values ?? {}).some(([key, value]) => (
        !SEARCH_SOURCE_ONLY_FIELDS.has(key) && hasSearchCriterionValue(value)
    ));
}

// buildDynamicFormData — builds the multipart FormData payload to send to the
// Auth gateway.
//
//   • File fields are appended as separate FormData parts. The part name is the
//     field's `fileKey` if configured, else the first segment of its path
//     (e.g. "passport.passportUploadName" → "passport"). Files nested inside an
//     addRow group array are collected from every row.
//   • All other fields are turned into the JSON "json" part by the shared,
//     config-driven payload engine (buildPayload). There is ZERO field-name or
//     module-specific logic here — payloadKey / dataType / payloadMode / template
//     are all read from the admin form-group config.
//
// Dynamic by design: any new field added in the admin panel is automatically
// included — no code change required. New modules work the same way.
function buildDynamicFormData(values, groups, extraPayload = {}, module = '') {
    const formData = new FormData();

    // New files → multipart parts, via the SHARED engine helper. On CREATE we
    // attach only the flat (non-repeatable) file fields — resume, photo, jobs
    // "file", passport, visa. Repeatable (addRow) document files are uploaded in
    // a follow-up update (see submit) because the create handler mishandles them;
    // the indexed update path is the reliable one. The document METADATA still
    // rides along in the JSON payload here, so the rows are created on insert and
    // the follow-up update just attaches each file to its row by index.
    collectFileParts(values, groups, { scope: 'flat', module }).forEach(({ formKey, file }) => {
        formData.append(formKey, file);
    });

    // Config-driven JSON payload (file fields are skipped by the engine).
    // extraPayload carries linkage/context keys that are NOT form fields but the
    // downstream create requires (e.g. submission needs jobId + applicantId). These
    // are merged first so a real form field of the same name still wins.
    const payload = { ...extraPayload, ...buildPayload(values, groups, { module }) };
    payload.schemaVersion = 1;

    formData.append('json', JSON.stringify(payload));
    return formData;
}

function FieldControl({ field, form, name, moduleName, value, checked, onChange, scopeValues = {}, onAiUpload, onDuplicateBlur, onRegionBlocked, onRowOrderChange, formDisabled, rowFieldKeys }) {
    const type = field.type ?? 'text';
    const baseDisabled = !editable(field, moduleName);
    const [dynamicOptions, setDynamicOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [lookupSearch, setLookupSearch] = useState('');
    const [masterSearch, setMasterSearch] = useState('');
    // Bumped after a quick create/edit ("Add More") so the dropdown refetches its
    // options and the freshly created/edited record appears immediately.
    const [refetchNonce, setRefetchNonce] = useState(0);
    const [viewer, setViewer] = useState({ open: false, docs: [], index: 0 });
    const [inputError, setInputError] = useState(null);
    const rawLookupRef = useRef([]);
    // lookupFilterValueFrom resolves against the SAME addRow row first (so a
    // per-row cascade like employerId → that row's recruiter list works), then
    // falls back to the top-level path — which is what every pre-existing
    // top-level config used. Both are always watched (hooks can't be
    // conditional); the row value wins whenever it is non-empty.
    const insideListRow = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
    const topFilterPath = field.lookupFilterValueFrom ? namePathFromString(field.lookupFilterValueFrom) : [`__lookup_filter_${field.field}`];
    const rowFilterPath = (field.lookupFilterValueFrom && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.lookupFilterValueFrom)]
        : ['__noop_row_filter__'];
    const topFilterValue = Form.useWatch(topFilterPath, form);
    const rowFilterValue = Form.useWatch(rowFilterPath, form);
    const formFilterValue = !empty(rowFilterValue) ? rowFilterValue : topFilterValue;
    // Watch sibling field for disabledByField — e.g. endDate disabled when currentlyPursuing is checked
    const disabledBySiblingPath = field.disabledByField
        ? (Array.isArray(name) && name.length > 1 ? [...name.slice(0, -1), field.disabledByField] : [field.disabledByField])
        : ['__noop_disabled_watch__'];
    const disabledByValue = Form.useWatch(disabledBySiblingPath, form);
    // disabledIf — conditional read-only, same operator set as showIf (eq/neq/
    // truthy/falsy/in/notIn), e.g. lock the Employer to the configured default
    // when Contract Type is W2. The row value wins when the condition field
    // lives in this row; otherwise the top-level field drives it.
    const disabledIfTopPath = field.disabledIf?.field ? namePathFromString(field.disabledIf.field) : ['__noop_disabled_if__'];
    const disabledIfRowPath = (field.disabledIf?.field && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.disabledIf.field)]
        : ['__noop_disabled_if_row__'];
    const disabledIfTop = Form.useWatch(disabledIfTopPath, form);
    const disabledIfRow = Form.useWatch(disabledIfRowPath, form);
    const conditionallyDisabled = field.disabledIf?.field
        ? labelConditionMatches(field.disabledIf, !empty(disabledIfRow) ? disabledIfRow : disabledIfTop)
        : false;
    // optionsFromField (admin config) — narrow this select's options to the live
    // value of ANOTHER field (e.g. the candidate rate unit may only be the unit
    // the linked job was raised in, which prefillFromModule already puts on the
    // form). Resolved with the same scoping a Show Condition uses: the row's own
    // field when the key belongs to this row (rowFieldKeys), the top-level field
    // otherwise — with a row-first fallback when membership is unknown, matching
    // lookupFilterValueFrom above. Both paths are always watched (hooks cannot be
    // conditional) so the dropdown re-narrows the moment the source changes.
    const constraintKey = field.optionsFromField || '';
    const constraintTopPath = constraintKey ? namePathFromString(constraintKey) : ['__noop_options_from__'];
    const constraintRowPath = (constraintKey && insideListRow && (!rowFieldKeys || rowFieldKeys.has(constraintKey)))
        ? [...name.slice(0, 2), ...namePathFromString(constraintKey)]
        : ['__noop_options_from_row__'];
    const constraintTopValue = Form.useWatch(constraintTopPath, form);
    const constraintRowValue = Form.useWatch(constraintRowPath, form);
    const constraintValue = !empty(constraintRowValue) ? constraintRowValue : constraintTopValue;
    // prefillWhenExclusive (admin config) — "a value a Conditional Default rule
    // would SET is EXCLUSIVE to that rule's condition". While the rule matches,
    // its value is offered (and PrefillWhenWatcher auto-selects it, as today);
    // while it does not, the value is REMOVED from the list instead of merely
    // being a stale default. Reuses field.prefillWhen so the hidden value and
    // the auto-filled value can never drift apart. Default off — a field
    // without the flag offers exactly what it offers today.
    //
    // HOOK SAFETY: the rule list is admin data, so it must never decide how
    // many hooks run. ONE selector-form useWatch reads every referenced field
    // out of the whole store and reduces them to a STRING (a fresh array each
    // render would churn the memo); the rule count changes what the selector
    // reads, never the hook count. Same row-vs-top-level scoping as
    // optionsFromField above: a rule key belonging to this addRow row resolves
    // to the row's own field, anything else (the top-level contract type read
    // from inside repeatable employer rows) to the top-level path.
    const exclusivePrefillRules = (truthy(field.prefillWhenExclusive) && field.prefillWhen?.length)
        ? field.prefillWhen
        : null;
    const exclusivePrefillKey = Form.useWatch(
        (values) => (exclusivePrefillRules
            ? JSON.stringify(prefillWhenWatchFields(exclusivePrefillRules).map((key) => {
                const read = (path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), values);
                const rowValue = (insideListRow && (!rowFieldKeys || rowFieldKeys.has(key)))
                    ? read([...name.slice(0, 2), ...namePathFromString(key)])
                    : undefined;
                return (!empty(rowValue) ? rowValue : read(namePathFromString(key))) ?? null;
            }))
            : ''),
        { form, preserve: true },
    );
    const exclusivePrefillWatched = useMemo(() => {
        if (!exclusivePrefillRules || !exclusivePrefillKey) return null;
        const values = JSON.parse(exclusivePrefillKey);
        const watched = {};
        prefillWhenWatchFields(exclusivePrefillRules).forEach((key, i) => { watched[key] = values[i]; });
        return watched;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exclusivePrefillKey]);
    const groupDisabled = useContext(GroupDisabledContext);
    // exclusiveInGroup — only ONE row of a repeatable group may hold this flag
    // (e.g. "Currently Working Here" / "Currently Pursuing": a person has one
    // current employer and one course in progress). Once any row claims it, the
    // control is disabled on every OTHER row, so the state cannot become
    // ambiguous. The claiming row keeps its control enabled — that is how the
    // user releases the claim and moves it elsewhere.
    //
    // Watching the whole Form.List (not one sibling) is what makes this react
    // to a change in a DIFFERENT row.
    const exclusiveListPath = (field.exclusiveInGroup && insideListRow)
        ? [name[0]]
        : ['__noop_exclusive_watch__'];
    const exclusiveRows = Form.useWatch(exclusiveListPath, form);
    const claimedByAnotherRow = useMemo(() => {
        if (!field.exclusiveInGroup || !insideListRow || !Array.isArray(exclusiveRows)) return false;
        const myIndex = name[1];
        const leaf = name[name.length - 1];
        return exclusiveRows.some((row, index) => index !== myIndex && truthy(row?.[leaf]));
    }, [field.exclusiveInGroup, insideListRow, exclusiveRows, name]);
    const disabled = baseDisabled || Boolean(field.disabled) || conditionallyDisabled || (field.disabledByField ? truthy(disabledByValue) : false) || Boolean(formDisabled) || groupDisabled || claimedByAnotherRow;
    // scopeValues only wins when it actually carries a value — a key present
    // but undefined/empty must fall back to the field's own live form value,
    // or the lookup filter is permanently starved and never resolves options.
    const scopeFilterValue = field.lookupFilterValueFrom
        ? scopeValues[field.lookupFilterValueFrom]
        : undefined;
    const lookupFilterValue = !empty(scopeFilterValue) ? scopeFilterValue : formFilterValue;

    // Fetch options dynamically when dataSource is master / module / lookup.
    // Static and undefined sources skip the API call and use field.options directly.
    useEffect(() => {
        if (type === 'location') {
            let cancelled = false;
            setOptionsLoading(true);

            async function fetchLocations() {
                try {
                    const params = new URLSearchParams({
                        search: locationSearch,
                        limit: String(field.limit ?? 50),
                        offset: '0',
                    });
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/location-dropdown-values?${params}`);
                    const raw = json?.data ?? json ?? [];
                    if (!cancelled) setDynamicOptions(normalizeOptions(Array.isArray(raw) ? raw : []));
                } catch (err) {
                    console.error(`[AddFormV1] Failed to fetch locations for "${field.field}":`, err);
                    if (!cancelled) setDynamicOptions([]);
                } finally {
                    if (!cancelled) setOptionsLoading(false);
                }
            }

            fetchLocations();
            return () => { cancelled = true; };
        }
        const ds = field.dataSource;
        if (!['select', 'radio', 'checkbox'].includes(type)) return undefined;
        if (!ds || ds === 'static') return undefined;
        if (ds === 'lookup' && field.lookupFilterField && empty(lookupFilterValue)) {
            setDynamicOptions([]);
            setOptionsLoading(false);
            return undefined;
        }

        // lookupMinSearchLength (opt-in): don't preload the full list on
        // mount — wait until the user has actually typed enough characters.
        // Existing lookup fields without this flag keep today's behavior
        // (full list shown immediately) untouched.
        if (ds === 'lookup' && field.lookupMinSearchLength && lookupSearch.trim().length < field.lookupMinSearchLength) {
            setDynamicOptions([]);
            setOptionsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setOptionsLoading(true);

        async function fetchOptions() {
            try {
                let raw = [];
                if (ds === 'master' || ds === 'module') {
                    raw = await getDropdownValues(
                        moduleName,
                        field.field,
                        ds === 'master' ? masterSearch : '',
                        field.limit ?? 50,
                        0,
                        {
                            dataSource: ds,
                            masterName: field.masterName,
                            groupName: field.groupName,
                        },
                    );
                } else if (ds === 'lookup') {
                    const params = new URLSearchParams({
                        collection: field.lookupCollection ?? '',
                        displayField: field.displayField ?? '',
                        valueField: field.valueField ?? '_id',
                    });
                    if (field.displayField2) params.set('displayField2', field.displayField2);
                    if (lookupSearch) params.set('search', lookupSearch);
                    if (field.lookupFilterField && !empty(lookupFilterValue)) {
                        params.set('filterField', field.lookupFilterField);
                        params.set('filterValue', String(lookupFilterValue));
                    }
                    if (Array.isArray(field.filters) && field.filters.length > 0) {
                        params.set('filters', JSON.stringify(field.filters));
                    }
                    // The row-filter config, rebuilt from the individual keys
                    // rather than passing `field` itself: every key here is a
                    // dependency of this effect, so the options refetch and
                    // re-filter whenever an admin changes one of them.
                    const rowFilterConfig = {
                        field: field.field,
                        extraFields: field.extraFields,
                        optionsRequireExtraField: field.optionsRequireExtraField,
                        optionsRequireExtraValue: field.optionsRequireExtraValue,
                        optionsHierarchyParentField: field.optionsHierarchyParentField,
                        optionsHierarchySelfFrom: field.optionsHierarchySelfFrom,
                    };
                    // Every extraData key the row filters need is requested
                    // automatically alongside the explicit extraFields, so a
                    // filter key can never be configured without its data.
                    const extraKeys = lookupExtraFieldKeys(rowFilterConfig);
                    if (extraKeys.length) params.set('extraFields', extraKeys.join(','));
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
                    raw = json?.data ?? json ?? [];
                    // Config-driven row filters: value match on an extraData key
                    // (e.g. only list a recruiter once verified, or only a given
                    // role) and/or the signed-in user's reporting downline.
                    raw = filterLookupOptionRows(raw, rowFilterConfig, { source: 'AddFormV1' });
                } else if (ds === 'group') {
                    const gn = (field.groupName ?? '').toLowerCase().trim();
                    if (gn === 'users' || gn === 'user') {
                        const json = await fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0');
                        const users = json?.data?.users ?? json?.users ?? json?.data ?? [];
                        raw = Array.isArray(users) ? users.map((u) => ({
                            label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.userName || u.email || String(u.userId),
                            value: u.userId ?? u.id,
                        })) : [];
                    }
                }
                if (!cancelled) {
                    rawLookupRef.current = Array.isArray(raw) ? raw : [];
                    setDynamicOptions(normalizeOptions(rawLookupRef.current));
                }
            } catch (err) {
                console.error(`[AddFormV1] Failed to fetch options for "${field.field}":`, err);
                if (!cancelled) setDynamicOptions([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }

        fetchOptions();
        return () => { cancelled = true; };
    }, [type, field.dataSource, field.field, field.limit, field.lookupCollection, field.displayField, field.displayField2, field.valueField, field.lookupFilterField, lookupFilterValue, field.groupName, field.masterName, locationSearch, lookupSearch, masterSearch, moduleName, field.extraFields, field.optionsRequireExtraField, field.optionsRequireExtraValue, field.optionsHierarchyParentField, field.optionsHierarchySelfFrom, refetchNonce, field.lookupMinSearchLength]);

    const staticOptions = normalizeOptions(field.options ?? field.values ?? []);
    const baseOptions = dynamicOptions ?? staticOptions;

    // ── Stored values outside the filtered option list ──────────────────────
    // `lookupFilters` decide what may be PICKED. They must not decide what an
    // already-assigned value is allowed to SHOW — a job assigned to somebody
    // outside the current user's reporting chain rendered its raw id ("149")
    // instead of a name. Resolved separately, unfiltered, and appended.
    const [resolvedOptions, setResolvedOptions] = useState([]);
    const missingValues = missingOptionValues(value, baseOptions);
    // Joined into a string: the array identity changes every render, and a raw
    // array dependency would refetch forever.
    const missingKey = missingValues.join(',');
    useEffect(() => {
        if (!missingKey || field.dataSource !== 'lookup' || optionsLoading) return undefined;
        let cancelled = false;
        fetchLookupLabels(field, missingKey.split(','))
            .then((rows) => { if (!cancelled) setResolvedOptions(rows); })
            // Best effort. Failing to resolve a label must never break the
            // field — the value is still there and still submits.
            .catch(() => { if (!cancelled) setResolvedOptions([]); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingKey, field.dataSource, field.lookupCollection, field.displayField,
        field.displayField2, field.valueField, optionsLoading]);
    // optionsFromField constraint applied here so EVERY option-driven control
    // (select, radio, checkbox group, location…) is narrowed the same way.
    // Fails open — see narrowOptionsByConstraint. prefillWhenExclusive then
    // COMPOSES on top of that narrowing (it removes, it never restores).
    const optionsWithStored = mergeResolvedOptions(baseOptions, resolvedOptions);
    const constrainedOptions = constraintKey ? narrowOptionsByConstraint(optionsWithStored, constraintValue) : optionsWithStored;
    const options = exclusivePrefillRules
        ? stripExclusivePrefillOptions(constrainedOptions, exclusivePrefillRules, exclusivePrefillWatched ?? {})
        : constrainedOptions;

    // Select multiplicity is config-driven: multiSelect flag → explicit mode →
    // field.multiple → array dataType. No field-name list. "tags" is opt-in via
    // an explicit field.mode === "tags".
    const selectMode = field.multiSelect
        ? 'multiple'
        : (field.mode ?? (truthy(field.multiple) ? 'multiple' : undefined));
    const inferredSelectMode = selectMode ?? (effectiveDataType(field) === 'array' ? 'multiple' : undefined);
    // allowCustomValues (admin config) — the field keeps a value the user TYPED
    // even when it is not in the list (Candidate → Skills is the first user, but
    // nothing here is module- or field-specific: any select/lookup field can opt
    // in from Form Groups). Until now free entry existed only for the hardcoded
    // `type: 'tagify'` control, which is not per-field selectable and loses every
    // other select behaviour (lookup, autofill, quick-create).
    const allowCustomValues = truthy(field.allowCustomValues);
    // antd's "tags" mode is what keeps a typed value; it always emits an ARRAY,
    // so a single-valued field has to be unwrapped again on the way out (see
    // handleSelectChange) and wrapped on the way in.
    const effectiveSelectMode = allowCustomValues ? 'tags' : inferredSelectMode;
    const isArrayValued = Boolean(field.multiSelect) || truthy(field.multiple)
        || effectiveDataType(field) === 'array' || inferredSelectMode === 'multiple';
    // Config-driven separators that commit a typed entry (default comma).
    const tokenSeparators = allowCustomValues
        ? (typeof field.tokenSeparators === 'string' && field.tokenSeparators
            ? field.tokenSeparators.split('')
            : [','])
        : undefined;

    const isSelectLike = ['select', 'location'].includes(type);
    const placeholder = field.hidePlaceholder
        ? undefined
        : (field.placeholder ?? `${isSelectLike ? 'Select' : 'Enter'} ${String(field.label ?? '').toLowerCase()}`);
    const inputLimits = getInputLimits(field);
    // Sets the value in the form store first, then re-validates.
    // Required because FieldControl is a custom wrapper — Form.Item's injected
    // onChange never reaches the inner Ant Design components, so the store
    // would otherwise hold stale/undefined values when validation runs.
    const updateAndValidate = (nextValue) => {
        onChange?.(nextValue);

        setTimeout(() => {
            form.validateFields([name]).catch(() => { });
        }, 0);
    };

    // Config-driven "Add More"/"Edit" quick actions inside a select dropdown.
    // Called unconditionally (rules of hooks) — returns nulls unless the field
    // opts in via quickCreate/quickEdit. On success, refetch options and (for a
    // fresh create on a single select) select the new record.
    const quick = useQuickField({
        field, form, name, moduleName, scopeValues, ownValue: value,
        onDone: (kind, newId) => {
            setRefetchNonce((n) => n + 1);
            const single = effectiveDataType(field) !== 'array' && !field.multiSelect && !field.multiple;
            if (kind === 'create' && newId != null && single) {
                // Same clearing the ordinary onChange does. Creating an employer
                // from inside the dropdown IS changing the employer, so the rest
                // of the group — recruiter, email, contact, VMS — still describes
                // the PREVIOUS one and must go. This path set the new value and
                // skipped the clear, so a brand-new employer kept the old
                // employer's recruiter beside it, which reads as a real pairing.
                //
                // Cleared BEFORE the value is written, so anything the new
                // selection autofills is preserved rather than wiped after.
                if (field.clearGroupOnChange || field.linkedClearField) {
                    applyGroupClear(form, field, name, rowFieldKeys);
                }
                updateAndValidate(usesLabeledSelectValue(field) ? { value: newId } : newId);
            }
        },
    });

    // Build validator config once (null when not set)
    const validatorCfg = field.validator?.type
        ? { ...field.validator, title: field.validator.title || field.label || 'Field' }
        : null;

    const inputProps = {
        ...inputLimits,
        disabled,
        placeholder,
        prefix: field.icon ? null : fieldIcons[field.field],
        value: value ?? '',
        onChange: (event) => {
            let nextValue = field.formatter
                ? cleanValue(event.target.value, field.formatter)
                : event.target.value;

            if (validatorCfg) {
                const { cleaned, error } = applyInputValidator(nextValue, validatorCfg, 'change');
                setInputError(error);
                nextValue = cleaned;
            }

            updateAndValidate(nextValue);
        },
        // Composed, not conditional. The character-validator blur only exists
        // when a validator is configured, so hanging the duplicate check off it
        // would silently skip every field without one — including a plain email
        // box, which is exactly the field this is for.
        onBlur: (e) => {
            if (validatorCfg) {
                const { cleaned, error } = applyInputValidator(e.target.value, validatorCfg, 'blur');
                setInputError(error);
                if (cleaned !== e.target.value) updateAndValidate(cleaned);
            }
            // Typing an email that already exists should be caught here, not on
            // submit after the whole form has been filled in.
            if (field.checkDuplicateOnBlur && String(e.target.value ?? '').trim()) {
                onDuplicateBlur?.(field);
            }
        },
        ...(validatorCfg && {
            onKeyPress: (e) => {
                const pattern = getKeyPattern(validatorCfg.type);
                if (!pattern) return;
                if (e.which === 0 || e.keyCode === 8 || e.keyCode === 13) return;
                const char = String.fromCharCode(e.which ?? e.keyCode);
                if (!pattern.test(char)) {
                    e.preventDefault();
                    const hint = `${validatorCfg.title} — invalid character.`;
                    setInputError(hint);
                    setTimeout(() => setInputError((prev) => (prev === hint ? null : prev)), 1800);
                }
            },
            maxLength: validatorCfg.maxLength ?? inputLimits.maxLength,
        }),
    };

    if (type === 'tagify') {
        return (
            <Select
                value={value}
                allowClear
                disabled={disabled}
                mode="tags"
                placeholder={placeholder}
                onChange={updateAndValidate}
            />
        );
    }
    if (type === 'location') {
        return (
            <Select
                value={value}
                allowClear
                showSearch
                filterOption={false}
                disabled={disabled}
                loading={optionsLoading}
                options={options}
                placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onSearch={setLocationSearch}
                onFocus={() => setLocationSearch((current) => current)}
                onChange={updateAndValidate}
            />
        );
    }
    if (type === 'select') {
        const remoteLookup = field.dataSource === 'lookup';
        const remoteMaster = field.dataSource === 'master';
        const remoteSearch = remoteLookup || remoteMaster;
        const keepLabel = usesLabeledSelectValue(field);
        const rawControlValue = toSelectControlValue(field, value, options);
        // "tags" mode renders from an array. A single-valued field that opted
        // into allowCustomValues still stores a scalar, so wrap it for display
        // only — the stored shape never changes.
        const controlValue = (allowCustomValues && !isArrayValued)
            ? (rawControlValue === undefined || rawControlValue === null || rawControlValue === ''
                ? undefined
                : (Array.isArray(rawControlValue) ? rawControlValue : [rawControlValue]))
            : rawControlValue;
        const handleSelectChange = (raw) => {
            // …and unwrap on the way out: keep the LAST entry the user typed or
            // picked, so a single-valued field never starts storing an array
            // just because free entry was switched on.
            const val = (allowCustomValues && !isArrayValued && Array.isArray(raw))
                ? (raw.length ? raw[raw.length - 1] : undefined)
                : raw;
            updateAndValidate(val);
            // Changing this field can invalidate the rest of its group: an
            // employer's recruiter, email, contact and VMS all describe the
            // PREVIOUS employer, and leaving them behind silently attaches one
            // company's details to another. Cleared BEFORE autofill below, so a
            // new selection's own values are written straight back in.
            if (field.clearGroupOnChange || field.linkedClearField) {
                applyGroupClear(form, field, name, rowFieldKeys);
            }
            if (field.autofillFrom?.length) {
                const selectedValue = val && !Array.isArray(val) && typeof val === 'object' ? val.value : val;
                const isInsideList = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
                if (val == null) {
                    // Cleared — wipe all autofill targets
                    field.autofillFrom.forEach(({ targetField: tf }) => {
                        const tp = isInsideList
                            ? [...name.slice(0, 2), ...namePathFromString(tf)]
                            : namePathFromString(tf);
                        form.setFieldValue(tp, null);
                    });
                } else if (rawLookupRef.current.length > 0) {
                    const vField = field.valueField ?? '_id';
                    const rawItem = rawLookupRef.current.find(item =>
                        getNestedProp(item, vField) === selectedValue || item._id === selectedValue || item.id === selectedValue || item.value === selectedValue
                    );
                    if (rawItem) {
                        field.autofillFrom.forEach(({ optionKey, targetField: tf }) => {
                            const v = getNestedProp(rawItem, optionKey) ?? rawItem.extraData?.[optionKey] ?? null;
                            const tp = isInsideList
                                ? [...name.slice(0, 2), ...namePathFromString(tf)]
                                : namePathFromString(tf);
                            form.setFieldValue(tp, v);
                        });
                    }
                }
            }
        };
        return (
            <>
                <Select
                    value={controlValue}
                    labelInValue={keepLabel || undefined}
                    allowClear
                    // Every select is type-to-search. It used to be enabled only
                    // for remote/lookup/group sources, so a static-option field
                    // (Currency, Unit, Work Mode, Status…) could only be picked
                    // by scrolling — painful once a list runs to 30 entries, as
                    // Relevant Experience does. Local lists filter on the LABEL
                    // (optionFilterProp); remote ones keep filterOption=false
                    // because the server already filtered.
                    // `field.showSearch: false` turns it off for a specific field.
                    showSearch={field.showSearch !== false}
                    optionFilterProp="label"
                    filterOption={remoteSearch ? false : undefined}
                    disabled={disabled}
                    loading={optionsLoading}
                    mode={effectiveSelectMode}
                    tokenSeparators={tokenSeparators}
                    options={options}
                    placeholder={placeholder}
                    notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                    onSearch={remoteLookup ? setLookupSearch : remoteMaster ? setMasterSearch : undefined}
                    onChange={handleSelectChange}
                    dropdownRender={quick.footer ? (menu) => (<>{menu}{quick.footer}</>) : undefined}
                />
                {quick.modal}
            </>
        );
    }
    if (type === 'radio') {
        return (
            <Radio.Group
                value={value}
                disabled={disabled}
                options={options}
                onChange={(e) => updateAndValidate(e.target.value)}
            />
        );
    }
    if (type === 'checkbox') {
        if (options.length > 0) {
            return (
                <Checkbox.Group
                    value={value}
                    disabled={disabled}
                    options={options}
                    onChange={updateAndValidate}
                />
            );
        }
        return (
            <Checkbox
                checked={Boolean(checked ?? value)}
                disabled={disabled}
                onChange={(e) => {
                    const next = e.target.checked;
                    // Region gate: some markets do not accept an entry that is
                    // still in progress. The tick is REVERSED rather than left
                    // standing with an error underneath — leaving it ticked
                    // invites the user to try to submit and be refused twice.
                    if (next && onRegionBlocked?.(field)) return;
                    updateAndValidate(next);
                    // Ticking "still ongoing" makes this the most recent entry,
                    // so it may now belong at the top of a newest-first list.
                    if (next) onRowOrderChange?.(field, name);
                    if (field.linkedClearField && next) {
                        const clearPath = Array.isArray(name) && name.length > 1
                            ? [...name.slice(0, -1), field.linkedClearField]
                            : [field.linkedClearField];
                        form.setFieldValue(clearPath, null);
                    }
                }}
            >
                {field.checkboxLabel ?? field.label}
            </Checkbox>
        );
    }
    if (type === 'text-editor') {
        return (
            <>
                <TipTapEditor
                    value={value ?? form.getFieldValue(name) ?? ''}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(html) => {
                        if (validatorCfg) {
                            const { error } = applyInputValidator(html, validatorCfg, 'change');
                            setInputError(error);
                        }
                        updateAndValidate(html);
                    }}
                />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }
    if (type === 'textarea') {
        return (
            <>
                <TextArea {...inputProps} rows={field.rows ?? 3} className="v1-scroll-textarea" />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }
    if (type === 'date') {
        return (
            <DatePicker
                disabled={disabled}
                placeholder={placeholder}
                style={{ width: '100%' }}
                suffixIcon={<CalendarOutlined />}
                value={value ?? null}
                disabledDate={getDisabledDate(field, form, name)}
                defaultPickerValue={getDefaultPickerValue(field)}
                onChange={(date) => {
                    updateAndValidate(date);
                    // Dates are the other way a row becomes the most recent
                    // one — the tick-box is not the only trigger.
                    onRowOrderChange?.(field, name);
                }}
            />
        );
    }
    if (type === 'time') return <TimePicker disabled={disabled} placeholder={placeholder} style={{ width: '100%' }} value={value ?? null} onChange={(time) => updateAndValidate(time)} />;
    if (type === 'file') {
        const fileRules = field.validations ?? field.validation ?? field.rules ?? [];
        const fileTypeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileType');
        const fileSizeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileSize');
        const maxFilesRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'maxFiles');

        // Admin "Allowed Content" category (field.accept) takes priority over the
        // fine-grained fileType rule; 'any'/unset falls through to the rule.
        const acceptCategory = uploadAcceptCategory(field);
        const acceptTypes = acceptCategory
            ? acceptCategory.exts.map((e) => `.${e}`).join(',')
            : (fileTypeRule?.value
                ? fileTypeRule.value.split(',').map((e) => `.${e.trim().replace(/^\./, '')}`).join(',')
                : undefined);
        const maxSizeMB = fileSizeRule ? Number(fileSizeRule?.value ?? 5) : undefined;
        // maxFiles validation rule takes top priority; otherwise use field.multiple/maxCount
        // (set via admin panel toggle), falling back to single-file mode.
        const maxFiles = maxFilesRule
            ? Number(maxFilesRule?.value ?? 1)
            : (truthy(field.multiple) ? (field.maxCount ?? 5) : 1);

        const fileList = Array.isArray(value) ? value : [];
        const canAddMore = fileList.length < maxFiles;

        const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        };

        const getFileIcon = (fileName) => {
            const ext = (fileName ?? '').split('.').pop()?.toLowerCase();
            if (ext === 'pdf') return <FilePdfOutlined className="v1-file-type-icon v1-file-icon-pdf" />;
            if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="v1-file-type-icon v1-file-icon-word" />;
            if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="v1-file-type-icon v1-file-icon-excel" />;
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FileImageOutlined className="v1-file-type-icon v1-file-icon-image" />;
            return <FileOutlined className="v1-file-type-icon v1-file-icon-default" />;
        };

        const handleFileDelete = (index) => {
            updateAndValidate(fileList.filter((_, i) => i !== index));
        };

        const handleFileView = (file, fileIdx) => {
            const docs = fileList.map((f) => {
                let url = f.url ?? f.thumbUrl ?? '';
                if (!url && f.originFileObj instanceof File) url = URL.createObjectURL(f.originFileObj);
                return { url, name: f.name ?? 'file' };
            }).filter(d => d.url);
            setViewer({ open: true, docs, index: fileIdx ?? 0 });
        };

        const isMulti = maxFiles > 1;
        const remaining = maxFiles - fileList.length;

        const typeHint = acceptCategory
            ? acceptCategory.hint
            : (acceptTypes
                ? acceptTypes.split(',').map((t) => t.replace('.', '').toUpperCase()).join(', ')
                : null);
        const sizeHint = maxSizeMB ? `up to ${maxSizeMB} MB` : null;
        const baseHint = [typeHint, sizeHint].filter(Boolean).join(' ');
        const dropHint = isMulti && fileList.length > 0
            ? [baseHint, `${remaining} of ${maxFiles} remaining`].filter(Boolean).join(' • ')
            : baseHint;
        const dropTitle = fileList.length > 0
            ? `Add ${isMulti ? 'more files' : 'file'}`
            : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

        const beforeUploadHandler = (file) => {
            if (fileList.length >= maxFiles) return Upload.LIST_IGNORE;
            if (acceptTypes) {
                const ext = (file.name.split('.').pop() ?? '').toLowerCase();
                const allowed = acceptTypes.split(',').map((t) => t.replace(/^\./, '').toLowerCase());
                if (!allowed.includes(ext)) {
                    message.error(acceptCategory
                        ? acceptCategory.error
                        : (fileTypeRule?.message ?? `Only ${fileTypeRule.value} files are allowed`));
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSizeMB !== undefined && file.size / 1024 / 1024 > maxSizeMB) {
                message.error(fileSizeRule?.message ?? `File must be smaller than ${maxSizeMB} MB`);
                return Upload.LIST_IGNORE;
            }
            return false;
        };

        // antd calls onChange once per file even in multi-select, each time with the
        // full accumulated newList. Slicing to maxFiles guards against racing additions.
        const handleUploadChange = ({ fileList: newList }) => {
            const sliced = newList.slice(0, maxFiles);
            updateAndValidate(sliced);
            // An "upload"-trigger AI action (e.g. auto-parse a resume) fires once
            // per newly-added file, using the raw browser File object.
            if (onAiUpload && sliced.length > fileList.length) {
                const added = sliced[sliced.length - 1];
                if (added?.originFileObj) onAiUpload(added.originFileObj);
            }
        };

        const multiUploadProps = {
            accept: acceptTypes,
            fileList,
            showUploadList: false,
            multiple: true,
            maxCount: maxFiles,
            beforeUpload: beforeUploadHandler,
            onChange: handleUploadChange,
            disabled,
        };

        const fileRows = fileList.map((file, index) => (
            <div key={file.uid ?? index} className="v1-file-item">
                <div className="v1-file-item-icon">{getFileIcon(file.name)}</div>
                <div className="v1-file-item-info">
                    <span className="v1-file-item-name" title={file.name}>{file.name}</span>
                    <span className="v1-file-item-meta">
                        {file.existing
                            ? 'Previously uploaded'
                            : `${formatFileSize(file.size)}${file.size ? ' • ' : ''}Uploaded just now`}
                    </span>
                </div>
                <Space size={6} className="v1-file-item-actions">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleFileView(file, index)}
                        className="v1-file-view-btn"
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(index)}
                        className="v1-file-delete-btn"
                    />
                </Space>
            </div>
        ));

        const viewerEl = (
            <DocumentViewer
                documents={viewer.docs}
                open={viewer.open}
                onClose={() => setViewer(v => ({ ...v, open: false }))}
                initialIndex={viewer.index}
            />
        );

        if (isMulti) {
            const multiTitle = fileList.length > 0
                ? `Upload more ${field.label ?? 'files'}`
                : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

            return (
                <>
                    <div className="v1-file-upload-wrapper">
                        {/* Centered dropzone — always at top in multiple mode */}
                        {canAddMore && (
                            <Upload {...multiUploadProps} style={{ width: '100%', display: 'block' }}>
                                <div className={`v1-upload-dropzone v1-upload-dropzone-center${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                    <div className="v1-upload-dropzone-title-row">
                                        <CloudUploadOutlined className="v1-upload-cloud-icon v1-upload-cloud-icon-sm" />
                                        <span className="v1-upload-dropzone-title v1-upload-dropzone-title-blue">{multiTitle}</span>
                                    </div>
                                    <span className="v1-upload-dropzone-sub">Drag & drop or click to upload</span>
                                    {baseHint && <span className="v1-upload-dropzone-sub">{baseHint}</span>}
                                </div>
                            </Upload>
                        )}

                        {/* Uploaded file rows — 2-column grid */}
                        {fileList.length > 0 && (
                            <div className="v1-file-grid">
                                {fileRows}
                            </div>
                        )}

                        {/* "+ Add more files" text link at the bottom */}
                        {canAddMore && fileList.length > 0 && (
                            <Upload {...multiUploadProps} style={{ display: 'inline-block' }}>
                                <Button type="link" size="small" icon={<PlusOutlined />} className="v1-add-more-files-btn">
                                    Add more files
                                </Button>
                            </Upload>
                        )}
                    </div>
                    {viewerEl}
                </>
            );
        }

        return (
            <>
                <div className="v1-file-upload-wrapper">
                    {/* Single mode: compact file row at field height */}
                    {fileList.length > 0 && <div className="v1-file-rows-single">{fileRows}</div>}

                    {canAddMore && (
                        <Upload
                            accept={acceptTypes}
                            fileList={fileList}
                            showUploadList={false}
                            multiple={false}
                            maxCount={1}
                            beforeUpload={beforeUploadHandler}
                            onChange={handleUploadChange}
                            disabled={disabled}
                            style={{ width: '100%', display: 'block' }}
                        >
                            <div className={`v1-upload-dropzone v1-upload-dropzone-single${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                <CloudUploadOutlined className="v1-upload-cloud-icon" />
                                <span className="v1-upload-dropzone-title">{dropTitle}</span>
                                {dropHint && <span className="v1-upload-dropzone-sub">— {dropHint}</span>}
                            </div>
                        </Upload>
                    )}
                </div>
                {viewerEl}
            </>
        );
    }

    return (
        <>
            <Input
                {...inputProps}
                type={type === 'number' ? 'number' : type}
                // step="any" so the browser's own number input accepts a
                // fractional value on a field the admin marked decimal.
                {...(type === 'number' && truthy(field.decimal) ? { step: 'any' } : {})}
                // Admin-configured unit shown inside the input (e.g. "%").
                {...(field.suffix ? { suffix: field.suffix } : {})}
            />
            {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
        </>
    );
}

function SectionTitle({ icon, title }) {
    return (
        <Space size={10} align="center">
            <div className="v1-section-icon-box">{icon}</div>
            <Text strong className="v1-section-label">{title}</Text>
        </Space>
    );
}

function FieldLabel({ field, label, locked }) {
    if (!label) return label;
    // field.tooltip (admin config) OR the older infoEnabled/infoText pair — one
    // resolver (fieldTooltipText) so both forms and every control type agree on
    // where a field's help text comes from. The TEXT is always admin config.
    const infoText = fieldTooltipText(field);
    const showInfo = Boolean(infoText);
    if (!showInfo && !locked) return label;
    return (
        <span className="v1-field-label-with-info">
            <span>{label}</span>
            {showInfo && (
                <Tooltip title={infoText}>
                    <InfoCircleOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
            {locked && (
                <Tooltip title="You don't have permission to edit this field">
                    <LockOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
        </span>
    );
}

function GroupInfoMessage({ group }) {
    if (!truthy(group?.infoEnabled) || empty(group?.infoText)) return null;
    return <Text type="secondary" className="v1-group-info-message">{group.infoText}</Text>;
}

function GroupToggleControl({ group }) {
    if (!truthy(group?.toggleEnabled) || empty(group?.toggleField)) return null;
    return (
        <Space size={6} align="center" onClick={(event) => event.stopPropagation()}>
            <Text type="secondary" className="v1-combine-toggle-label">{group.toggleLabel || 'Toggle'}</Text>
            <Form.Item name={group.toggleField} valuePropName="checked" initialValue={Boolean(group.toggleValue)} noStyle>
                <Switch size="small" />
            </Form.Item>
        </Space>
    );
}


// renderFieldHelp — the guidance under a field.
//
// `helpText` accepts a STRING or an ARRAY of strings. An array renders as
// stacked lines, which is what lets one field carry two different KINDS of
// help without them running together: the mechanical constraint ("Supports
// .pdf, .doc, .docx up to 2MB") and the reason to bother ("Upload the résumé
// first — we can read the details straight out of it"). Jammed into one
// paragraph they read as a wall and the second half is skipped.
//
// A plain string keeps rendering exactly as before, so nothing existing moves.
function renderFieldHelp(field) {
    const raw = field?.helpText;
    const lines = (Array.isArray(raw) ? raw : [raw])
        .map((line) => (line == null ? '' : String(line).trim()))
        .filter(Boolean);
    if (!lines.length) return undefined;
    return (
        <span className="v1-field-help-text">
            {lines.map((line, i) => (
                <Text key={i} type="secondary" className="v1-field-help-line">{line}</Text>
            ))}
        </span>
    );
}

// forwardRef, not a plain `ref` prop: this package declares `react >=18` as its
// peer, and on React 18 a function component does not receive `ref` as a prop.
// React 19 no longer needs the wrapper but still honours it, so this is the one
// form that works across the whole supported range.
//
// Exported inline rather than via a `const AddFormV1 = forwardRef(...)` binding:
// a const of the same name shadows the render function, and the transform then
// renames the inner one ("AddFormV12"), which is what DevTools and every error
// stack would go on to display.
export default forwardRef(function AddFormV1({
    moduleName,
    action,
    authToken,
    clientId,
    region,
    formGroups: providedFormGroups,
    loading: providedLoading = false,
    error: providedError = '',
    breadcrumbItems = [],
    // formType — controls the mode of operation:
    //   'add'    → Add Form (default, existing behavior)
    //   'search' → Search Form (lighter validation, Reset button, no auto-create)
    formType = 'add',
    submitText: propSubmitText,
    cancelPath = '/candidates',
    onSubmit,
    // initialRecord — prefill flat form fields from a plain record object.
    // Matched by field.field / payloadKey / dot-path; best-effort, never required.
    initialRecord = null,
    // prefillFromUrl — when set (quickSubmit flow), AddFormV1 reads the route to
    // decide the prefill source and the job/candidate linkage entirely on its own:
    //   • isFromSubmission=true + submissionId → read the submissions collection
    //   • otherwise candidateId                → read the candidates collection
    // Both resolve through getFormGroups(id) (field.value + group.rows embedded) —
    // no separate detail-view API. The linkage the submission create requires
    // (applicantId + jobId) is derived from the same URL params.
    prefillFromUrl = false,
    // extraPayload — context/linkage keys merged into the create payload that are
    // NOT form fields (e.g. submission needs { jobId, applicantId }). Kept out of
    // the form so the config stays generic. Ignored when prefillFromUrl derives
    // its own linkage from the route.
    extraPayload = null,
    // presetClientId — caller already knows the client (e.g. Quick Submit knows
    // it from the selected job) so the client-scope gate should resolve silently
    // instead of showing the manual "Select Client" picker.
    presetClientId = null,
    // ListView props for search mode — passed through from parent when search results
    // should be displayed below the search form.
    listViewProps = {},
    // selectedScope — optional scope parameter for filtering search results
    selectedScope = '',
    // searchActionContext — page-level context (e.g. { jobId }) merged into every
    // search-result row action's popupState, so a popup opened from here (like
    // Source Candidates' "Shortlisted"/"Tag to Jobs") already knows what the
    // caller knows instead of asking the user to re-select it. Mirrors the
    // action.popupState convention ListView already spreads into the popup's
    // navigation state — see ListView.jsx's handleActionClick.
    searchActionContext = null,
    // embedded — render the form for use inside a modal (quick "Add More" from a
    // select dropdown). Suppresses page chrome/navigation; on a successful create
    // onSuccess(newRecordId, values) is called instead of navigating away, and the
    // Cancel button calls onCancel. Entirely opt-in; the standalone page path is
    // unchanged when embedded is false.
    embedded = false,
    onSuccess = null,
    onCancel = null,
}, ref) {
    useEffect(() => {
        if (authToken) setAuthTokenOverride(authToken);
        return () => {
            if (authToken) clearAuthTokenOverride();
        };
    }, [authToken]);

    const isSearch = formType === 'search';
    const submitText = propSubmitText ?? (isSearch ? 'Search' : 'Create');
    const navigate = useNavigate();
    // Router location — nav state carries the in-flight relevant-skill match
    // handed over by Quick Submit (see the prefill effect below).
    const routerLocation = useLocation();
    // Cache for the relevant-skill match promise, keyed by its token — see the
    // prefill effect below and services/pendingHandoff.js.
    const skillMatchTokenRef = useRef(null);
    const skillMatchPromiseRef = useRef(null);
    const routeParams = useParams();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();

    // ── Imperative handle ───────────────────────────────────────────────────
    // Lets a host drive the form from outside it — submit it from its own
    // button, validate before advancing a wizard step, read the current values
    // for a preview, or reset it after a modal closes.
    //
    // Deliberately only these four. Exposing `form` itself would hand callers
    // the entire antd API, including setFieldsValue — and a host writing
    // directly into the store bypasses every conditional default, payload gate
    // and linked-clear rule this component maintains, in ways nothing here
    // could see or correct.
    //
    // submit() goes through form.submit() rather than calling the submit
    // handler, so an imperative submit runs validation, the duplicate gate and
    // the after-submit navigation exactly as pressing the button does.
    useImperativeHandle(ref, () => ({
        submit: () => form.submit(),
        validate: () => form.validateFields(),
        getValues: () => form.getFieldsValue(true),
        reset: () => form.resetFields(),
    }), [form]);

    const searchFormValues = Form.useWatch([], form);
    const [searchLoading, setSearchLoading] = useState(false);
    const [collapsed, setCollapsed] = useState({});
    const [apiFormGroups, setApiFormGroups] = useState([]);
    const [apiLoading, setApiLoading] = useState(Boolean(moduleName));
    const [apiError, setApiError] = useState('');
    const [searchFormLabel, setSearchFormLabel] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    // Search results state — used when formType === 'search' to store and display results
    const [searchResults, setSearchResults] = useState({
        searched: false,
        rows: [],
        total: 0,
        fields: [],
        actions: [],
        actionRules: [],
        loading: false,
    });
    const [searchPagination, setSearchPagination] = useState({ current: 1, pageSize: 10 });
    const [lastSearchValues, setLastSearchValues] = useState({});
    const [selectedSource, setSelectedSource] = useState('internal');
    const [tabs, setTabs] = useState([]);
    const [sourceActionState, setSourceActionState] = useState({});
    const [clientOptions, setClientOptions] = useState([]);
    const [scopeClientId, setScopeClientId] = useState(clientId ?? '');
    const [scopeRegion, setScopeRegion] = useState(region ?? '');
    // URL-driven prefill state — populated when prefillFromUrl is set. Holds the
    // source record's form groups (candidate OR submission) with values embedded.
    const [prefillGroups, setPrefillGroups] = useState(null);
    const [prefillLoading, setPrefillLoading] = useState(false);
    // Role Configure → Form permissions for the CURRENT user's role, derived
    // server-side from this module's Form Groups config (see
    // GetRoleFormPermissions). null until loaded / when unavailable, in which
    // case normalizeGroups' rolePerms checks fail open — no behavior change
    // for a role/module that hasn't been configured.
    const [rolePerms, setRolePerms] = useState(null);
    // Default Fields (Form Groups → Default Fields) — always-shown + required
    // field keys for this module. Empty set until loaded / when unconfigured.
    const [defaultFieldSet, setDefaultFieldSet] = useState(() => new Set());
    const formGroups = moduleName ? apiFormGroups : (providedFormGroups ?? emptyGroups);
    const loading = moduleName ? apiLoading : providedLoading;
    const error = moduleName ? apiError : providedError;
    // ── Client-scoped form render, resolved from the linked JOB ──────────────
    // A submission is always created against a job, and the job owns the client.
    // So when the form is opened with a jobId (/submission-create/:jobId, Quick
    // Submit, Source Candidates…) we look the job up, read its clientId, and use
    // it as THIS form's client scope. getFormGroups then returns the client's
    // Form Groups variant when the admin has configured one for that client, and
    // transparently falls back to the module default when they haven't (the
    // backend tries clientId+region → clientId → region → default), so this is
    // safe for every client that has no override.
    const [linkedClientId, setLinkedClientId] = useState('');
    const linkedJobId = (routeParams?.jobId && routeParams.jobId !== '_')
        ? routeParams.jobId
        : (searchParams.get('jobId') || '');

    useEffect(() => {
        // An explicit client from the caller always wins — nothing to resolve.
        if (clientId || presetClientId || !linkedJobId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const data = await getFormGroups({ module: 'jobs', id: linkedJobId });
                const record = flatRecordFromGroups(extractGroups(data)) ?? {};
                // A reference field comes back either as a raw id or {id,value}.
                const raw = record.clientId ?? record.client ?? '';
                const resolved = (raw && typeof raw === 'object') ? (raw.id ?? raw.value ?? '') : raw;
                if (!cancelled && resolved) setLinkedClientId(String(resolved));
            } catch (err) {
                console.error('Client-scope resolve from job failed:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [clientId, presetClientId, linkedJobId]);

    const effectiveClientId = clientId || presetClientId || linkedClientId || scopeClientId;
    const effectiveRegion = region || scopeRegion;

    // Load the common display defaults (phone format, date format, …) once so a
    // phone field re-formats to the admin-configured mask. Cached; safe to call.
    useEffect(() => { loadDetailDefaults(); }, []);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setRolePerms(null); return; }
            // Same client/region scope the form's own structure is fetched
            // with (getFormGroups below) — a client-scoped Form Groups
            // override must gate the SAME scoped structure, not the
            // tenant-wide default.
            getRoleFormPermissions({ roleId: localStorage.getItem('roleId') }, moduleName, { clientId: effectiveClientId, region: effectiveRegion })
                .then((data) => { if (!cancelled) setRolePerms(Array.isArray(data) && data.length ? data : null); })
                .catch(() => { if (!cancelled) setRolePerms(null); });
        });
        return () => { cancelled = true; };
    }, [moduleName, effectiveClientId, effectiveRegion]);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setDefaultFieldSet(new Set()); return; }
            getModuleDefaultFields(moduleName)
                .then((keys) => { if (!cancelled) setDefaultFieldSet(new Set(Array.isArray(keys) ? keys : [])); })
                .catch(() => { if (!cancelled) setDefaultFieldSet(new Set()); });
        });
        return () => { cancelled = true; };
    }, [moduleName]);

    const groups = useMemo(() => normalizeGroups(formGroups, moduleName, rolePerms, defaultFieldSet), [formGroups, moduleName, rolePerms, defaultFieldSet]);

    // ── Config-driven field uniqueness ──────────────────────────────────────
    // One checker per form session owns the request sequence, the
    // normalized-value cache and the pending count (see
    // form/uniqueFieldValidation.js). While a check is in flight the submit
    // button is disabled so a record can never be created on a value whose
    // answer has not come back yet. A search form never checks uniqueness.
    const [uniquePending, setUniquePending] = useState(0);
    const uniqueChecker = useMemo(
        () => createUniqueChecker({ checkFieldUnique, onPendingChange: setUniquePending }),
        [],
    );
    const uniqueContext = useMemo(() => (isSearch ? undefined : {
        checker: uniqueChecker,
        module: moduleName,
        clientId: effectiveClientId || undefined,
        region: effectiveRegion || undefined,
        // NO recordId on the Add form — there is no record to exclude yet.
    }), [isSearch, uniqueChecker, moduleName, effectiveClientId, effectiveRegion]);
    // Every unique-configured field in this form, used to map a submit-time
    // duplicate back onto its field when the response body was flattened.
    const uniqueRuleIndex = useMemo(() => collectUniqueRules(groups), [groups]);

    // group.linkGroup — addRow groups sharing the same value stay row-synced
    // (see form/linkedAddRowGroups.js). Empty for every group with no
    // linkGroup, i.e. every group that predates this feature.
    const linkedGroupSets = useMemo(() => computeLinkedSets(groups), [groups]);
    // Registry of each addRow group's live Form.List {add, remove}, written on
    // every render from inside the Form.List's own render-prop (synchronous
    // within the same commit, so it's fully populated before a user can click
    // anything). A linked group's "Add Row" / row-delete calls every member's
    // add()/remove() through this registry; unlinked groups just call their own.
    const listControlsRef = useRef({});
    // When an ADDITIONAL submit button (group.formActions, e.g. "Create and
    // Source Candidate") is clicked, it stashes its own afterSubmit here so the
    // shared submit() navigates per that button instead of the group default.
    // Reset after every submit and by the plain Create button.
    const pendingAfterSubmitRef = useRef(null);
    const formActions = useMemo(() => groups.flatMap((g) => g.formActions ?? []), [groups]);
    // "Have we got this person already?" — asked right after a résumé fills the
    // form in (the first moment an email/phone exist to check), and again on
    // submit as the authoritative gate. Config-driven; a module with no
    // duplicate stages configured never sees any of it.
    const duplicateConfig = useMemo(() => {
        const groupWithConfig = extractGroups(formGroups).find((g) => g?.duplicateCheck);
        return groupWithConfig?.duplicateCheck ?? {};
    }, [formGroups]);

    const duplicates = useDuplicateCheck({
        form,
        moduleName,
        config: duplicateConfig,
        // Choosing "update the existing record" hands off to that record's Edit
        // form, carrying the file just uploaded so it is stored as another
        // version rather than being lost with the abandoned Add form.
        // The edit route is CONFIG, never guessed. There is no derivable rule:
        // this app routes candidates at "/candidate/edit/:id" (singular) and
        // clients at "/clients/edit/:id" (plural). Deriving it sent every
        // candidate to "/candidates/edit/:id", which matches no route, so the
        // catch-all redirected to the dashboard — the record the user asked to
        // open simply vanished.
        //
        // With no route configured we do NOT navigate. Closing the dialog and
        // leaving the user where they are is recoverable; throwing them at a
        // wrong URL is not.
        onUseExisting: useCallback((id, result, reason, carry) => {
            if (!id) return;
            // The backend delivers the configured route WITH the answer, so
            // there is one source of truth; the group-level value is only an
            // override for a project that wants a different destination.
            const template = result?.editRoute || duplicateConfig?.editRoute;
            if (!template) {
                console.warn(
                    `[duplicate-check] ${moduleName}: no editRoute configured, so the existing `
                    + 'record was not opened. Set it in Admin → Automations → "Check for someone '
                    + 'already on file" → "Where to open the existing record".',
                );
                return;
            }
            const target = safeNavTarget(template, id, {});
            if (!target) return;
            // The uploaded file travels WITH the user. They picked that résumé
            // for that person; sending them to the existing record and quietly
            // dropping it would make them find and attach it again — and the
            // whole point of opening the existing record is to add this newer
            // file to it (file versioning keeps the previous one).
            navigate(target, {
                state: {
                    fromDuplicate: true,
                    carryFiles: carry?.files ?? null,
                },
            });
        }, [duplicateConfig, navigate]),
    });

    const aiActions = useAiActions({
        form,
        groups,
        module: moduleName,
        // BEFORE any parsed value reaches the form. This is the first moment an
        // email and phone exist, so it is the first moment the question can be
        // asked — and if we already have this person there is no point filling
        // in a form the user is about to abandon.
        onParsed: useCallback(async ({ field, payload }) => {
            const key = field?.field;
            // The file the user just picked, handed to the prompt so it can
            // travel with them to the existing record.
            const carry = key ? { files: { [key]: form.getFieldValue(key) } } : null;

            const verdict = await duplicates.prompt({ reason: 'upload', payload, carry });
            if (verdict === 'clear') return true;

            // 'existing' — they were sent to the record we already have and the
            // file went WITH them, so it must not also be left behind here.
            // 'blocked' — they backed out to check; leaving the file attached to
            // a form that was never filled in just means the next thing they do
            // starts from a half-state they did not choose.
            // Either way the upload is undone: nothing on this form was filled
            // in, so nothing on it should look as though it was.
            if (key) {
                form.setFieldValue(key, []);
                message.info(verdict === 'existing'
                    ? 'Résumé moved to their record.'
                    : 'Résumé removed — nothing on this form was filled in.');
            }
            return 'abort';
        }, [duplicates, form]),
        // AFTER the form is filled — a second, cheaper pass that also covers
        // anything the user had typed before uploading.
        onApplied: useCallback(async () => {
            await duplicates.prompt({ reason: 'upload' });
        }, [duplicates]),
    });

    // Blur on a field marked `checkDuplicateOnBlur` (the email box). Debounced
    // through a ref rather than fired directly: tabbing quickly through a form
    // can blur the same field twice, and two overlapping checks would open two
    // dialogs on top of each other.
    // ── Region gate ─────────────────────────────────────────────────────────
    // The active region (Admin → Detail Groups → Display Defaults → Region)
    // selects which `regionRules` block each group applies. A region is a KEY
    // into config, never a branch in code — which is what lets a new market be
    // added without touching this file.
    //
    // Today's use: a candidate may be put forward mid-course in the US and UK,
    // but not in India, where a company only accepts a completed qualification.
    const handleRegionBlocked = useCallback((field) => {
        const region = getDefaults().region;
        // Find the group this "still ongoing" tick-box belongs to, so its own
        // region rules decide — not some global setting.
        const group = groups.find((g) => (g.fields ?? []).some((f) => f.field === field.field));
        if (!group) return false;

        const effective = resolveRegionRules(group, region);
        // Only gates the field the group nominates as its ongoing flag, so an
        // unrelated checkbox in the same group is never caught by this.
        const ongoingField = effective.orderBy?.inProgressField ?? 'currentStudyingHere';
        if (field.field !== ongoingField) return false;
        if (inProgressAllowed(effective)) return false;

        // Warn vs block. A warning explains the consequence and lets the tick
        // stand — recording a real person mid-degree is worth doing even where
        // they could not be put forward today. A block reverses the tick, and
        // the submit-time validator below refuses the row however it arrived.
        const blocking = inProgressBlocks(effective);
        openFormNotice({
            tone: blocking ? 'duplicate' : 'overwrite',
            title: blocking
                ? 'This has to be completed first'
                // States the CONSEQUENCE, not a vague heading. The user's next
                // action depends on knowing that saving this is fine but
                // submitting them later will not be.
                : 'You can add this, but they cannot be submitted',
            facts: [
                { label: 'Region', value: region || 'Common' },
                { label: 'Entry', value: group.label ?? group.name },
            ],
            body: inProgressError(effective),
            okText: blocking ? 'Got it' : 'Keep it anyway',
        });
        return blocking;
    }, [groups]);

    // ── The submit-time half of the same rule ───────────────────────────────
    // Reversing the tick-box only catches a row somebody ticked BY HAND. A
    // submission prefills its rows from the candidate, and the candidate is
    // allowed to hold an in-progress entry — so on a blocking region the row
    // arrives already ticked and nobody ever touched the box. Without a rule on
    // the field the form would happily submit it, and the restriction would
    // apply to exactly the users who did not need stopping.
    //
    // Resolved here rather than inside getRules because it depends on the
    // GROUP, which getRules never sees.
    const inProgressRulesFor = useCallback((field, groupName) => {
        const group = groups.find((g) => g.name === groupName);
        if (!group) return undefined;
        const effective = resolveRegionRules(group, getDefaults().region);
        const ongoingField = effective.orderBy?.inProgressField ?? 'currentStudyingHere';
        if (field.field !== ongoingField) return undefined;
        const rule = buildInProgressValidator(effective);
        return rule ? [rule] : undefined;
    }, [groups]);


    // ── Newest-first row ordering ───────────────────────────────────────────
    // After the user changes a row's dates or ticks "still ongoing", check
    // whether that row now belongs somewhere else and ASK. Nothing is ever
    // reordered silently — a list that rearranges itself while you are typing
    // in it is disorienting, and the user may have a reason for the order.
    const handleRowOrder = useCallback(async (field, name) => {
        // Only meaningful inside a repeatable group: [listName, rowIndex, key].
        if (!Array.isArray(name) || name.length < 3 || typeof name[1] !== 'number') return;
        const groupName = name[0];
        const rowIndex = name[1];

        const group = groups.find((g) => g.name === groupName);
        if (!group) return;
        // Region-resolved, so a market that reorders differently is honoured.
        const effective = resolveRegionRules(group, getDefaults().region);
        if (!effective.orderBy) return;

        // Read AFTER the change has landed in the store, so the row being
        // judged carries the value the user just set rather than the previous
        // one — the whole decision turns on that value.
        await Promise.resolve();
        const rows = form.getFieldValue(groupName);
        if (!Array.isArray(rows)) return;

        await promptRowMove({
            group: effective,
            rows,
            rowIndex,
            move: listControlsRef.current[groupName]?.move,
            confirm: ({ title, body, okText, cancelText, from, to }) => openFormDecision({
                tone: 'suggestion',
                title,
                facts: [
                    { label: 'This entry is at', value: `Row ${from + 1}` },
                    { label: 'It belongs at', value: `Row ${to + 1}` },
                ],
                body,
                okText,
                cancelText,
            }),
        });
    }, [groups, form]);

    const duplicateBlurRef = useRef(false);
    const handleDuplicateBlur = useCallback(async () => {
        if (duplicateBlurRef.current) return;
        duplicateBlurRef.current = true;
        try {
            await duplicates.prompt({ reason: 'blur' });
        } finally {
            duplicateBlurRef.current = false;
        }
    }, [duplicates]);
    const [viewMode, setViewMode] = useState("list");
    const searchDisabled = isSearch && !hasSearchCriteria(searchFormValues);

    // Scan raw formGroups (before normalizeGroups strips the clientScope field) to
    // auto-detect whether this module needs a client gate and read its optional message.
    // The Admin field config is the only source that enables this gate.
    const { hasClientScope, clientGateMessage, clientGateResetOnChange } = useMemo(() => {
        const gateField = extractGroups(formGroups).flatMap(g => g.fields ?? []).find(f => truthy(f.clientScope));
        return gateField
            ? {
                hasClientScope: true,
                clientGateMessage: gateField.clientGateMessage || '',
                clientGateResetOnChange: truthy(gateField.clientGateResetOnChange),
            }
            : { hasClientScope: false, clientGateMessage: '', clientGateResetOnChange: false };
    }, [formGroups]);

    const handleClientGateChange = (nextClientId) => {
        if (clientGateResetOnChange) {
            form.resetFields();
            // resetFields restores configured initial/default values. Explicitly
            // clear every root key as well so changing the Client Gate truly
            // removes nested values, files and repeatable-group rows.
            const storedValues = form.getFieldsValue(true);
            form.setFieldsValue(Object.fromEntries(
                Object.keys(storedValues).map((fieldName) => [fieldName, undefined]),
            ));
            setSearchResults({ searched: false, rows: [], total: 0, fields: [], loading: false });
            setSearchPagination({ current: 1, pageSize: 10 });
        }
        setScopeClientId(nextClientId ?? '');
    };

    const waitingForClientScope = hasClientScope && !effectiveClientId;

    const loadClientOptions = useCallback(() => (
        getAdminClients({ limit: 200 }).then(setClientOptions).catch(() => setClientOptions([]))
    ), []);

    useEffect(() => {
        // No picker needed when the client is already known (caller-supplied or
        // resolved from the linked job).
        if (!hasClientScope || presetClientId || linkedClientId) return;
        loadClientOptions();
    }, [hasClientScope, presetClientId, linkedClientId, loadClientOptions]);

    // Quick-create hook for the "Select Client" gate — the same "Add More"
    // in-dropdown create used by ordinary lookup fields (useQuickField), so the
    // gate can open the Clients Add form in a modal and, on save, refetch the
    // options and select the new client. Reuses the generic clients Add flow.
    //
    // NOTE — this synthetic field object is HARDCODED (a pre-existing wart: the
    // client gate is rendered by this component rather than as an ordinary
    // configured field, so it has no admin field-config document to read).
    // Every OTHER field gets the same behaviour purely from admin config via the
    // generic `quickCreateNotice` key; only this one gate has to restate it in
    // code. The gate is the actual submission/job path the requirement is about,
    // so it must carry the notice too — leaving it out would mean the one flow
    // the product owner described was the one flow without the warning.
    const clientGateQuick = useQuickField({
        field: {
            field: 'clientId',
            quickCreate: true,
            quickCreateModule: 'clients',
            quickCreateLabel: 'Add Client',
            lookupCollection: 'clients',
            displayField: 'clientName',
            quickCreateNotice: {
                enabled: true,
                title: 'New Client Created',
                // message omitted on purpose — the built-in default IS the
                // product-owner-approved wording (see quickCreateNotice.js).
                showCreator: true,
                showRecordName: true,
                recordLabel: 'Client name',
                creatorLabel: 'Created by',
            },
        },
        form,
        name: 'clientId',
        moduleName,
        scopeValues: {},
        ownValue: effectiveClientId,
        onDone: (kind, newId) => {
            loadClientOptions();
            if (kind === 'create' && newId != null) handleClientGateChange(String(newId));
        },
    });

    useEffect(() => {
        if (!moduleName) return undefined;

        let cancelled = false;

        async function fetchFormGroups() {
            try {
                setApiLoading(true);
                setApiError('');
                const data = isSearch
                    ? await getCustomForms(moduleName, 'search', action)
                    : await getFormGroups({ module: moduleName, action, clientId: effectiveClientId, region: effectiveRegion });
                if (!cancelled) {
                    setApiFormGroups(extractGroups(isSearch ? data?.[0] : data));
                    if (isSearch) setSearchFormLabel(data?.[0]?.label || '');
                }
            } catch (err) {
                console.error(`Failed to fetch ${moduleName} form groups:`, err);
                if (!cancelled) setApiError(err?.message || 'Failed to fetch form groups');
            } finally {
                if (!cancelled) setApiLoading(false);
            }
        }

        fetchFormGroups();

        return () => {
            cancelled = true;
        };
    }, [moduleName, action, isSearch, effectiveClientId, effectiveRegion]);

    // ── URL-driven prefill source + linkage ──
    // Decode the route once. The submission flow arrives at
    //   /submission-create/:jobId?candidateId=…&isFromSubmission=…[&submissionId=…]
    //   • isFromSubmission=true + submissionId → prefill from the submissions record
    //   • otherwise candidateId                → prefill from the candidates record
    const urlPrefill = useMemo(() => {
        if (!prefillFromUrl) return null;

        const candidateId = searchParams.get('candidateId') || '';
        const submissionId = searchParams.get('submissionId') || '';
        const fromSubmission = String(searchParams.get('isFromSubmission')) === 'true';
        const jobId = (routeParams.jobId && routeParams.jobId !== '_')
            ? routeParams.jobId
            : (searchParams.get('jobId') || '');

        const source = (fromSubmission && submissionId)
            ? { module: 'submissions', id: submissionId }
            : (candidateId ? { module: 'candidates', id: candidateId } : null);

        // Linkage the submission create requires (NOT form fields).
        const linkage = {};
        if (candidateId) linkage.applicantId = candidateId;
        if (jobId) linkage.jobId = jobId;

        return { source, linkage: Object.keys(linkage).length ? linkage : null };
    }, [prefillFromUrl, searchParams, routeParams]);

    // ── Prefill fetch: load the source record's form groups with embedded values ──
    // The backend embeds field.value for scalar fields and group.rows for addRow
    // groups (workExperience, educationDetails, documents). Single source of truth
    // for BOTH the candidate and submission flows — no detail-view API call.
    useEffect(() => {
        const source = urlPrefill?.source;
        if (!source) return undefined;

        let cancelled = false;

        async function fetchPrefill() {
            try {
                setPrefillLoading(true);
                const data = await getFormGroups({ module: source.module, id: source.id });
                if (!cancelled) setPrefillGroups(extractGroups(data));
            } catch (err) {
                console.error('Failed to fetch prefill data:', err);
                if (!cancelled) setPrefillGroups([]);
            } finally {
                if (!cancelled) setPrefillLoading(false);
            }
        }

        fetchPrefill();

        return () => { cancelled = true; };
    }, [urlPrefill]);

    // addRow rows from the prefill source, keyed by group.payloadKey (e.g.
    // "workExperience"). Submission addRow groups that share the same payloadKey
    // receive these rows as their Form.List initialValue at mount. Works for both
    // sources because the DB array element keys are identical (companyName,
    // jobTitle, qualification, …).
    const addRowPrefillsByKey = useMemo(() => {
        if (!prefillGroups) return {};
        return Object.fromEntries(
            prefillGroups
                .filter(g => g.addRow && Array.isArray(g.rows) && g.rows.length > 0)
                .map(g => [g.payloadKey || g.name, g.rows]),
        );
    }, [prefillGroups]);

    // Flat record extracted from the prefill source's scalar fields — prefills
    // form fields whose field.field (or payloadKey) matches a source field.
    const prefillFlatRecord = useMemo(
        () => flatRecordFromGroups(prefillGroups),
        [prefillGroups],
    );

    // ── Cross-module context prefill (field.prefillFromModule) ──────────────
    // A field may prefill from a DIFFERENT module's record than the default
    // prefill source — e.g. submission.mspReqId ← the current JOB's mspreqId on
    // Quick Submit. The admin sets prefillFromModule (which module) +
    // prefillFrom (which key); the record id comes from the same URL linkage the
    // create payload already uses (jobId → jobs, applicantId → candidates).
    const contextPrefillModules = useMemo(() => {
        const wanted = new Set();
        groups.forEach((group) => (group.fields ?? []).forEach((field) => {
            if (field.prefillFromModule) wanted.add(normalizeModule(field.prefillFromModule));
        }));
        return [...wanted];
    }, [groups]);

    const [contextRecords, setContextRecords] = useState({});
    useEffect(() => {
        const linkage = urlPrefill?.linkage;
        if (!linkage || contextPrefillModules.length === 0) return undefined;
        let cancelled = false;
        // Shared with EditFormV1 (form/contextPrefill.js) — resolves <module>Id /
        // <singular>Id (+ known aliases like applicantId) out of the id map,
        // which is the URL linkage here and the record's own recordMeta there.
        contextPrefillModules.forEach(async (mod) => {
            const id = contextRecordId(mod, linkage);
            if (!id) return;
            try {
                const data = await getFormGroups({ module: mod, id });
                const record = flatRecordFromGroups(extractGroups(data));
                if (!cancelled && record) {
                    setContextRecords((prev) => ({ ...prev, [mod]: record }));
                }
            } catch (err) {
                console.error(`Context prefill fetch failed for ${mod}:`, err);
            }
        });
        return () => { cancelled = true; };
    }, [contextPrefillModules, urlPrefill]);

    // ── Prefill from initialRecord / prefillFlatRecord (best-effort) ──
    // Matches each non-file scalar field by field.field → payloadKey on the
    // source record and sets it via setFieldsValue. addRow groups are seeded via
    // Form.List initialValue (see getAddRowInitialValue below) — setFieldsValue
    // on a Form.List that mounted empty only keeps the last row.
    useEffect(() => {
        const sourceRecord = initialRecord ?? prefillFlatRecord;
        // Runs when EITHER the default source or a cross-module context record is
        // ready — a form with only prefillFromModule fields (no candidate source,
        // e.g. Quick Submit straight from a job) must still prefill those.
        if ((!sourceRecord && Object.keys(contextRecords).length === 0)
            || loading || groups.length === 0) return;
        const values = {};
        groups.forEach((group) => {
            if (group.toggleEnabled && group.toggleField && sourceRecord) {
                const stored = pickPrefillValue(sourceRecord, { field: group.toggleField });
                if (stored !== undefined && stored !== null) values[group.toggleField] = Boolean(stored);
            }
            if (group.addRow) return;
            (group.fields ?? []).forEach((field) => {
                if (!field.field) return;
                // A repeatable (addRow) field prefills via its Form.List
                // initialValue at mount — setFieldsValue on a Form.List that
                // mounted empty only keeps the last item, so skip it here.
                if (truthy(field.addRow)) return;
                // prefillFromModule redirects this field's source to another
                // module's URL-linked record (e.g. the current job).
                const fieldSource = field.prefillFromModule
                    ? contextRecords[normalizeModule(field.prefillFromModule)]
                    : sourceRecord;
                if (!fieldSource) return;
                // prefillFrom (admin config) → field → payloadKey, dotted or nested.
                const stored = pickPrefillValue(fieldSource, field);
                if (stored === undefined || stored === null) return;
                if (field.type === 'file') {
                    const fl = toFileListValue(stored);
                    if (fl.length > 0) writeFormValue(values, field.field, fl);
                } else {
                    // writeFormValue, NOT values[field.field]: a DOTTED field key
                    // ("candidateBudget.candidateBudgetCurrency") is registered as an
                    // ARRAY name path (see `field.field.split('.')` where `name` is
                    // built), but a flat object key containing dots is treated by
                    // rc-field-form as one literal field name. So the old assignment
                    // wrote a key nothing was listening on, and prefillFromModule /
                    // prefillFrom silently did NOTHING for every nested field — the
                    // value was fetched, mapped, and then dropped on the floor.
                    writeFormValue(values, field.field, buildInitialValue(field, stored));
                }
            });
        });
        if (Object.keys(values).length > 0) form.setFieldsValue(values);
    }, [initialRecord, prefillFlatRecord, contextRecords, loading, groups, form]);

    // ── Relevant-skill prefill (async, never blocking) ──────────────────────
    // Quick Submit starts the match at SUBMIT time and hands a TOKEN for the
    // still-in-flight promise over in nav state (the promise itself cannot
    // travel through router state — history.pushState requires
    // structured-cloneable data, and a Promise is not, see pendingHandoff.js).
    // The request overlaps this form's mount instead of delaying it. When it
    // settles we fill the rows of every addRow group the admin marked with
    // `skillMatchField` — that config key names the field inside the row that
    // receives the skill, so no group, field or module name is hardcoded here.
    //
    // Form.List rows must be written with setFieldsValue, never a remount: a
    // key change would throw away anything the recruiter has already typed.
    // Rows the user has already filled are left alone for the same reason.
    //
    // REFRESH SAFETY: history.state (and therefore the token) SURVIVES a browser
    // reload, but the in-memory map the token points into does not — the SPA
    // runtime that held it is gone. takePendingPromise then returns nothing and
    // the rows silently never fill, which is exactly what QA saw: "relevant
    // skills are not parsed after a refresh". So the INPUTS travel too, and when
    // the token resolves to nothing we simply re-issue the match. Inputs come
    // from nav state when the caller supplied them, else from the URL linkage
    // this form already derives (jobId in the path, candidateId in the query) —
    // which is itself refresh-proof. Still non-blocking, still driven only by
    // group.skillMatchField.
    useEffect(() => {
        const token = routerLocation?.state?.relevantSkillsToken;
        const stateInputs = routerLocation?.state?.relevantSkillsInputs;
        const jobId = stateInputs?.jobId ?? urlPrefill?.linkage?.jobId ?? '';
        const applicantId = stateInputs?.applicantId ?? stateInputs?.candidateId
            ?? urlPrefill?.linkage?.applicantId ?? '';
        // Guard on token/loading/groups BEFORE taking the promise — takePendingPromise
        // deletes on read, so calling it before we're actually ready to use the
        // result would consume it on a run that's just going to bail out, and
        // every later run (once loading clears / groups arrive) would find nothing.
        //
        // A handoff is still REQUIRED to start a match: without a token (or
        // explicit inputs in nav state) this is an ordinary visit to the form,
        // not a Quick Submit, and must not fire an AI call on its own.
        const handedOff = Boolean(token) || Boolean(stateInputs);
        if (!handedOff || loading || groups.length === 0) return undefined;

        const targets = groups.filter((g) => truthy(g.addRow) && g.skillMatchField);
        if (targets.length === 0) return undefined;

        // StrictMode double-invokes effects (mount → cleanup → mount again with
        // identical deps). Re-taking on the second mount would find the token
        // already consumed and silently skip the fill, so a same-token remount
        // reuses the promise already taken instead of consuming again. The same
        // ref also stops the refresh path from firing a SECOND match request on
        // the double-invoke.
        const runKey = token || `inputs:${jobId}:${applicantId}`;
        let promise;
        if (skillMatchTokenRef.current === runKey && skillMatchPromiseRef.current) {
            promise = skillMatchPromiseRef.current;
        } else {
            promise = token ? takePendingPromise(token) : undefined;
            // Handoff lost (page reloaded, or the link was opened directly) —
            // re-issue from the inputs. matchRelevantSkills never rejects and
            // degrades to [], which the fill below treats as "change nothing".
            if ((!promise || typeof promise.then !== 'function') && jobId && applicantId) {
                promise = matchRelevantSkills({ jobId, applicantId }).catch(() => []);
            }
            skillMatchTokenRef.current = runKey;
            skillMatchPromiseRef.current = promise;
        }
        if (!promise || typeof promise.then !== 'function') return undefined;

        let cancelled = false;
        promise.then((skills) => {
            // An empty result must NOT wipe the rows. "Nothing matched" and
            // "the service was unreachable" arrive here identically, and
            // clearing a candidate's carried-over skills because an AI call
            // failed would destroy real data.
            if (cancelled || !Array.isArray(skills) || skills.length === 0) return;
            const values = {};
            targets.forEach((group) => {
                const existing = form.getFieldValue(group.name);
                const rows = Array.isArray(existing) ? existing : [];
                // REPLACE, not append: the rows carried over from the candidate
                // are that candidate's skills in general, not the ones this job
                // asks for. The match is the answer to "which apply here", so
                // anything it did not return is dropped.
                //
                // A row whose skill IS in the match is REUSED rather than
                // rebuilt, so its Experience/Remarks survive — replacing it
                // with a bare { skill } would silently discard values the
                // recruiter (or the candidate record) already supplied.
                const bySkill = new Map();
                rows.forEach((row) => {
                    const key = String(row?.[group.skillMatchField] ?? '').trim().toLowerCase();
                    if (key && !bySkill.has(key)) bySkill.set(key, row);
                });
                values[group.name] = skills.map((skill) => (
                    bySkill.get(String(skill).trim().toLowerCase())
                    ?? { [group.skillMatchField]: skill }
                ));
            });
            if (Object.keys(values).length > 0) form.setFieldsValue(values);
        }).catch(() => { /* matchRelevantSkills already degrades to [] */ });

        return () => { cancelled = true; };
        // The linkage ids are listed as SCALARS: urlPrefill is a fresh memo
        // object on every searchParams change, so depending on it would re-run
        // (and, on the refresh path, re-request) for no reason.
    }, [
        routerLocation?.state?.relevantSkillsToken,
        routerLocation?.state?.relevantSkillsInputs,
        urlPrefill?.linkage?.jobId,
        urlPrefill?.linkage?.applicantId,
        loading, groups, form,
    ]);

    // Linkage merged into the create payload: URL-derived when prefillFromUrl,
    // else the explicit extraPayload prop.
    const scopePayload = hasClientScope && effectiveClientId ? { clientId: effectiveClientId } : {};
    const effectiveExtraPayload = { ...scopePayload, ...(urlPrefill?.linkage ?? extraPayload ?? {}) };

    // True once both the form groups and (when needed) the prefill source data are
    // ready. The Form.List components must NOT mount until this is true so that
    // getAddRowInitialValue below provides the correct initialValue at mount —
    // antd ignores initialValue changes after a Form.List has already mounted.
    const isFormReady = !waitingForClientScope && !loading && (!urlPrefill?.source || (prefillGroups !== null && !prefillLoading));

    // Build the Form.List initialValue for an addRow group. When candidate prefill
    // data is available the group's payloadKey (e.g. "workExperience") is looked up
    // in addRowPrefillsByKey and the stored rows are mapped through buildInitialValue
    // so antd controls receive the right shape. Falls back to a single empty row.
    const getAddRowInitialValue = (group) => {
        // prefillFrom lets an addRow group seed its rows from a DIFFERENT source
        // group key than its own (write) key — e.g. the submission employer group
        // (submission_current_employer) prefilling from the candidate's
        // current_employer_details rows on Quick Submit. Falls back to the write
        // key, so groups that already share a payloadKey are unaffected.
        const gKey = group.prefillFrom || group.payloadKey || group.name;
        const rows = addRowPrefillsByKey[gKey];
        const minRows = Math.max(0, Number(group.minRows ?? 0) || 0);
        // Admin-configured defaultValues seed every fresh row (top-level fields
        // get theirs via Form.Item initialValue, which does not reach Form.List
        // rows — e.g. VMS Commission 5.5 inside the Employer group).
        const seed = rowDefaultsFor(group);
        if (!rows || rows.length === 0) {
            // initialRows in config controls how many empty rows to start with (default 0).
            // minRows still controls the minimum kept after removal.
            const startCount = group.initialRows !== undefined
                ? Math.max(minRows, Math.max(0, Number(group.initialRows) || 0))
                : minRows;
            return Array.from({ length: startCount }, () => ({ ...seed }));
        }
        const initial = rows.map(row => {
            const rowVals = { ...seed };
            (group.fields ?? []).forEach(field => {
                if (!field.field || field.type === 'file') return;
                const val = pickPrefillValue(row, field);
                if (val !== undefined && val !== null) {
                    // A repeat field inside the row (nested Form.List) hydrates as
                    // an array, each element normalised for its control; a plain
                    // field hydrates as a single value.
                    rowVals[field.field] = truthy(field.addRow)
                        ? buildRepeatFieldInitial(field, val)
                        : buildInitialValue(field, val);
                }
            });
            return rowVals;
        });
        while (initial.length < minRows) initial.push({ ...seed });
        return initial;
    };

    // Precomputed per-group so a linked set (group.linkGroup) can be padded to
    // equal length BEFORE any Form.List mounts — antd ignores initialValue
    // changes after first mount, so this has to be ready up front, not derived
    // ad hoc per group. A no-op passthrough for every group not in a linked set.
    const addRowInitials = useMemo(() => {
        const raw = {};
        groups.forEach((group) => {
            if (group.addRow) raw[group.name] = getAddRowInitialValue(group);
        });
        return padLinkedInitialValues(linkedGroupSets, raw);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, addRowPrefillsByKey, linkedGroupSets]);

    const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleReset = () => {
        form.resetFields();
        // Also reset collapsed state to default (all expanded)
        setCollapsed({});
        setSelectedSource('internal');
        setTabs([]);
    };

    const handleSearch = useCallback(async (values, sourceOverride, paginationOverride = {}) => {
        if (!isSearch) return;
        const nextPageSize = Number(paginationOverride.pageSize) > 0
            ? Number(paginationOverride.pageSize)
            : searchPagination.pageSize;
        const requestedCurrent = Number(paginationOverride.current);
        const nextCurrent = Number.isFinite(requestedCurrent) && requestedCurrent > 0
            ? Math.floor(requestedCurrent)
            : 1;

        try {
            setLastSearchValues(values);
            setSearchPagination({ current: nextCurrent, pageSize: nextPageSize });
            setSearchResults((current) => ({ ...current, loading: true, searched: true }));
            const checkedSources = getSearchSources(values?.selectedSource);
            const activeSource = sourceOverride
                ? resolveSearchSource(sourceOverride, selectedSource)
                : (checkedSources.includes('internal')
                    ? 'internal'
                    : resolveSearchSource(values?.selectedSource, selectedSource));

            // Include selectedSource in the search parameters
            const searchParamsWithSource = {
                ...values,
                selectedSource: activeSource,
            };

            console.log('[AddFormV1] Calling searchModuleData with scope:', activeSource);

            const result = await searchModuleData(moduleName || 'candidates', searchParamsWithSource, {
                limit: nextPageSize,
                offset: (nextCurrent - 1) * nextPageSize,
            }, {
                scope: activeSource,
            });

            setSelectedSource(activeSource);
            setSearchResults({
                searched: true,
                rows: result.rows,
                total: result.total,
                fields: result.fields,
                // Admin-configured row actions/rules — searchModuleData pulls these
                // from the same /module-data-list response the regular ListView
                // list-endpoint returns them on, so search results get the same
                // admin-driven actions instead of none.
                actions: result.actions ?? [],
                actionRules: result.actionRules ?? [],
                loading: false,
            });
            setTabs((currentTabs) => buildSourceTabs({
                sources: checkedSources,
                activeSource,
                activeCount: result.total,
                responseCounts: result.counts,
                currentTabs,
                backendTabs: result.tabs,
            }));
        } catch (error) {
            console.error('Search failed:', error);
            message.error(error?.message || 'Failed to search records');
            setSearchResults({ searched: true, rows: [], total: 0, fields: [], actions: [], actionRules: [], loading: false });
        }
    }, [isSearch, moduleName, selectedSource, searchPagination.pageSize]);

    const handleSourceAction = useCallback(async (action, record) => {
        const actionKey = getSourceActionInstanceKey(action, record);
        setSourceActionState((current) => ({
            ...current,
            [actionKey]: { ...(current[actionKey] ?? {}), loading: true },
        }));
        try {
            const response = await runSourceAction(action, record);
            message.success(
                response?.message
                ?? response?.data?.message
                ?? action.successMessage
                ?? 'Action completed successfully.',
            );
            setSourceActionState((current) => ({
                ...current,
                [actionKey]: { loading: false, completed: true },
            }));
        } catch (error) {
            message.error(error?.message || action.errorMessage || 'Action failed.');
            setSourceActionState((current) => ({
                ...current,
                [actionKey]: { ...(current[actionKey] ?? {}), loading: false },
            }));
        } finally {
            setSourceActionState((current) => ({
                ...current,
                [actionKey]: { ...(current[actionKey] ?? {}), loading: false },
            }));
        }
    }, []);

    const handleTabChange = useCallback((newSource) => {
        const activeSource = resolveSearchSource(newSource, selectedSource);
        setSelectedSource(activeSource);
        setSearchPagination({ current: 1, pageSize: searchPagination.pageSize });

        // Re-trigger search with new source if we have previous search values
        if (Object.keys(lastSearchValues).length > 0) {
            handleSearch(lastSearchValues, activeSource, { current: 1, pageSize: searchPagination.pageSize });
        }
    }, [searchPagination.pageSize, lastSearchValues, handleSearch, selectedSource]);

    const handleSearchPageChange = useCallback((current, pageSize) => {
        const nextPageSize = Number(pageSize) > 0 ? Number(pageSize) : searchPagination.pageSize;
        const nextCurrent = Number(current) > 0 ? Number(current) : 1;
        if (Object.keys(lastSearchValues).length === 0) {
            setSearchPagination({ current: nextCurrent, pageSize: nextPageSize });
            return;
        }
        handleSearch(lastSearchValues, selectedSource, { current: nextCurrent, pageSize: nextPageSize });
    }, [handleSearch, lastSearchValues, searchPagination.pageSize, selectedSource]);

    const handleSearchPageSizeChange = useCallback((pageSize) => {
        const nextPageSize = Number(pageSize) > 0 ? Number(pageSize) : searchPagination.pageSize;
        if (Object.keys(lastSearchValues).length === 0) {
            setSearchPagination({ current: 1, pageSize: nextPageSize });
            return;
        }
        handleSearch(lastSearchValues, selectedSource, { current: 1, pageSize: nextPageSize });
    }, [handleSearch, lastSearchValues, searchPagination.pageSize, selectedSource]);

    // Submit-time duplicate ("already present") coming back from the
    // authoritative server-side check. The record was NOT created, so the form
    // must behave exactly like a failed client-side validation: values kept,
    // form open, collapsed sections expanded, error shown under the offending
    // field, and that field scrolled to + focused (shared
    // utils/scrollToFirstFormError, same as onFinishFailed).
    //
    // The name used is the one the SERVER returned — the frontend field key,
    // which is not necessarily the stored field it maps to.
    const applyDuplicateFieldErrors = (source) => {
        const duplicates = extractDuplicateFieldErrors(source, { uniqueRules: uniqueRuleIndex });
        if (duplicates.length === 0) return false;

        // item.name is the FULL antd path: a plain key for an ordinary field, and
        // [group, rowIndex, key] for a field inside a repeatable (addRow) group,
        // so the error lands on the row the user actually typed into.
        form.setFields(duplicates.map((item) => ({ name: item.name ?? item.field, errors: [item.message] })));
        setCollapsed({});
        scrollToFirstFormError({
            errorFields: duplicates.map((item) => {
                const name = item.name ?? item.field;
                return { name: Array.isArray(name) ? name : [name], errors: [item.message] };
            }),
        }, form);
        return true;
    };

    const submit = async (values) => {
        if (isSearch) {
            await handleSearch(values);
            return;
        }

        // The authoritative duplicate gate. The upload-time check is a courtesy
        // — it fires before the form is complete and only when a file was read
        // — so the decision is taken again here, against the finished values,
        // where it can actually stop a second copy being created.
        // 'clear' and 'existing' both continue: 'existing' means the user has
        // already been sent to the record we have, so this submit is moot.
        if (!embedded) {
            const verdict = await duplicates.prompt({ reason: 'submit' });
            if (verdict !== 'clear') return;
        }

        // A host page can own the confirm step (e.g. Quick Submit's Pontoon
        // attestation / preview modal) and POST the record itself. It still gets
        // the CONFIG-DRIVEN payload here — identical to what the native submit
        // below would send — so payloadMode ("skip"), payloadKey remapping,
        // dataType coercion and the showIf payload gating all still apply.
        // Passing only `values` (raw antd form state, as before) meant a page
        // with a custom onSubmit silently bypassed the entire Form Groups
        // config. `values` is still the first argument for callers that need the
        // raw state for display (preview cards, file lists).
        if (onSubmit) {
            await onSubmit(values, {
                moduleName,
                groups,
                // A host that POSTs the record itself (Quick Submit, Resubmit)
                // was silently skipping the shared document registry, because
                // this branch returns before the registration below ever runs.
                // The uploaded résumé therefore existed on the submission
                // record but nowhere in `documents`, so nothing could link it
                // to the candidate. Handing the host the SAME registration the
                // native path uses closes that gap without it having to know
                // how files are collected.
                registerDocuments: async (recordId, opts = {}) => {
                    const parts = collectFileParts(values, groups, { module: opts.module ?? moduleName });
                    if (!parts.length || !recordId) return [];
                    return uploadModuleDocuments(opts.module ?? moduleName, recordId, parts);
                },
                extraPayload: effectiveExtraPayload ?? {},
                payload: {
                    ...(effectiveExtraPayload ?? {}),
                    ...buildPayload(values, groups, { module: moduleName }),
                },
            });
            return;
        }

        if (!moduleName) {
            console.log('Form values:', values);
            return;
        }

        try {
            setSubmitLoading(true);
            const response = await createModuleRecord(moduleName, buildDynamicFormData(values, groups, effectiveExtraPayload ?? {}, moduleName));
            const success = response?.status !== false;

            if (!success) {
                // A duplicate reported in a 200 envelope ({status:false}) is the
                // same failure as a 409 — handle it the same way.
                if (applyDuplicateFieldErrors(response)) return;
                message.error(submitErrorMessage(groups, moduleName, 'add',
                    response?.error || response?.message || response?.data));
                return;
            }

            // Repeatable (addRow) document files are attached in a follow-up update
            // through the reliable indexed path (documents[0], documents[1], …). The
            // rows themselves were created on insert (metadata is in the create JSON),
            // so this only uploads each file to its row by index. Runs only when the
            // form actually carried addRow file uploads, so plain records and jobs
            // stay single-request.
            const addRowFileParts = collectFileParts(values, groups, { scope: 'addRow', indexed: true, module: moduleName });
            const newRecordId = response?.id ?? response?.data?.id ?? response?.data?._id ?? response?._id;
            if (addRowFileParts.length > 0 && newRecordId) {
                try {
                    await updateRecord(moduleName, newRecordId, { ...(effectiveExtraPayload ?? {}), ...buildPayload(values, groups, { module: moduleName }) }, addRowFileParts);
                } catch (uploadErr) {
                    console.error('Document file attach (post-create update) failed:', uploadErr);
                    message.warning('Record created, but attaching document files failed. Edit the record to re-upload.');
                }
            }

            // Common document registry — the module record is created FIRST (the
            // Create API above generated its reference id), then every uploaded
            // file (single or multiple, flat or addRow) is registered in the
            // shared "documents" collection against that id, keyed by module name
            // + the form field it was uploaded under (resume, jobDescription, …).
            // Best-effort: the record already exists, so a registry failure only
            // warns and never rolls back the create.
            const allFileParts = collectFileParts(values, groups, { module: moduleName });
            if (allFileParts.length > 0 && newRecordId) {
                try {
                    await uploadModuleDocuments(moduleName, newRecordId, allFileParts);
                } catch (docErr) {
                    console.error('Document registry upload failed:', docErr);
                    message.warning('Record created, but saving its document records failed.');
                }
            }

            // Admin-configurable success toast (group.submitMessages); defaults to
            // "<Module> has added successfully" for every module, new ones included.
            const successText = submitSuccessMessage(groups, moduleName, 'add');
            if (successText) message.success(successText);
            form.resetFields();

            // Embedded (quick "Add More" modal): hand the new id back to the caller
            // so it can refetch its option list and select the new record — never
            // navigate away from the host form.
            if (embedded && onSuccess) {
                onSuccess(newRecordId, values);
                return;
            }

            // A clicked extra button (formActions) overrides the group's default
            // afterSubmit for this submit; the plain Create button leaves the ref
            // null and keeps the existing behavior.
            const afterSubmit = pendingAfterSubmitRef.current ?? resolveAfterSubmit(groups);
            if (!afterSubmit) {
                // No admin-configured behavior: return to wherever this form was
                // opened FROM, falling back to cancelPath on a cold deep-link.
                //
                // This replaced a hardcoded module→route map (jobs→/jobs,
                // candidates→/candidates, …) — four module names living in a
                // component that is otherwise entirely config-driven, and which
                // silently did nothing at all for every other module.
                goBackOrTo(navigate, { cancelPath });
            } else if (afterSubmit.mode === 'back') {
                goBackOrTo(navigate, { cancelPath });
            } else if (afterSubmit.mode === 'route' || afterSubmit.mode === 'detail') {
                const target = safeNavTarget(afterSubmit.target, newRecordId, values);
                if (target) navigate(target);
            }
            // afterSubmit.mode === 'stay' (or an unresolvable target): no navigation.
        } catch (err) {
            console.error(`Failed to create ${moduleName}:`, err);
            // Authoritative server-side uniqueness check: keep every value the
            // user entered, keep the form open, mark the field and take them to
            // it. No success toast, no navigation.
            if (applyDuplicateFieldErrors(err)) return;
            message.error(submitErrorMessage(groups, moduleName, 'add',
                err?.data?.error || err?.data?.message || err?.message));
        } finally {
            setSubmitLoading(false);
            pendingAfterSubmitRef.current = null;
        }
    };

    const getFieldDependencies = (field, currentName, prefixFor) => {
        const rules = field.validations ?? field.validation ?? field.rules ?? [];
        // maxFromField is listed separately because its referenced field may live
        // OUTSIDE this row: the dependency path has to be built with the same
        // scoping the rule itself uses, not the sibling assumption below.
        const scoped = rules
            .filter((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'maxFromField')
            .map((rule) => maxFromFieldDependency(rule, currentName, prefixFor))
            .filter(Boolean);
        return [...scoped, ...rules
            .filter((rule) => {
                const type = typeof rule === 'string' ? rule : rule?.type;
                return ['dateAfterField', 'greaterThanField', 'strictGreaterThanField',
                    'strictLessThanField', 'lessThanOrEqualField', 'maxFieldWithFallback',
                    'dateBeforeField', 'timeAfterField', 'timeBeforeField'].includes(type);
            })
            .map((rule) => {
                const compareFields = String(rule.value ?? rule.compareField ?? rule.field ?? '')
                    .split(',').map((item) => item.trim()).filter(Boolean);
                return compareFields.map((compareField) => getSiblingFieldName(currentName, compareField));
            })
            .flat()
            .filter(Boolean)];
    };

    // Evaluate a Show Condition. `prefixFor` resolves the sibling path prefix for
    // a referenced field key (row-first for a group's own field, else top-level);
    // it may also be a precomputed prefix array/null for legacy single-condition
    // callers. A showIf can be a single leaf ({field,operator,value}) OR carry a
    // `conditions` array combined by `logic` ("and" default | "or") — the
    // multi-condition "add more" form — and a leaf + conditions combine too.
    const resolvePrefix = (prefixFor, key) => (typeof prefixFor === 'function' ? prefixFor(key) : prefixFor);
    const evalShowIfLeaf = (cond, getFieldValue, prefixFor) => {
        if (!cond?.field) return true;
        const prefix = resolvePrefix(prefixFor, cond.field);
        const path = prefix != null
            ? [...(Array.isArray(prefix) ? prefix : [prefix]), cond.field]
            : [cond.field];
        const val = getFieldValue(path);
        switch (cond.operator) {
            case 'eq': return String(val ?? '') === String(cond.value ?? '');
            case 'neq': return String(val ?? '') !== String(cond.value ?? '');
            case 'truthy': return val !== undefined && val !== null && val !== '' && val !== false;
            case 'falsy': return val === undefined || val === null || val === '' || val === false;
            // notEmpty targets an addRow GROUP's own Form.List name (cond.field is
            // that group's name, e.g. "submission_referral_details") — true once the
            // group has at least one row. Lets a flat field elsewhere in the form
            // (e.g. "referral rate") only appear/require once a referral row exists.
            case 'notEmpty': return Array.isArray(val) ? val.length > 0 : Boolean(val);
            // in/notIn: cond.value is a comma-separated list (e.g. "W2-Fulltime,W2-Contract").
            case 'in': return String(cond.value ?? '').split(',').map((v) => v.trim()).includes(String(val ?? ''));
            case 'notIn': return !String(cond.value ?? '').split(',').map((v) => v.trim()).includes(String(val ?? ''));
            default: return true;
        }
    };
    const evaluateShowIf = (showIf, getFieldValue, prefixFor) => {
        if (!showIf) return true;
        const conditions = Array.isArray(showIf.conditions) ? showIf.conditions.filter((c) => c?.field) : [];
        if (conditions.length) {
            const results = conditions.map((c) => evalShowIfLeaf(c, getFieldValue, prefixFor));
            const orLogic = String(showIf.logic ?? 'and').toLowerCase() === 'or';
            let combined = orLogic ? results.some(Boolean) : results.every(Boolean);
            // A top-level leaf alongside conditions is folded in with the same logic.
            if (showIf.field) {
                const leaf = evalShowIfLeaf(showIf, getFieldValue, prefixFor);
                combined = orLogic ? (combined || leaf) : (combined && leaf);
            }
            return combined;
        }
        return evalShowIfLeaf(showIf, getFieldValue, prefixFor);
    };
    // True when a field carries any Show Condition (single leaf or multi-condition).
    const hasShowIf = (showIf) => Boolean(showIf?.field || showIf?.conditions?.some?.((c) => c?.field));

    const [combineToggles, setCombineToggles] = useState({});

    const fieldKey = (field) => String(field?.field ?? '');

    // Who is filling this form — drives admin-configured field autoValueRules
    // (display only; the gateway enforces the same rules server-side).
    const actingIdentity = useMemo(() => getActingIdentity(), []);

    const renderField = (field, namePrefix, listName, opts = {}) => {
        // Split dotted keys into array form so AntD Form nests values correctly:
        // "passport.passportNumber" → ['passport','passportNumber']
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : null;
        const parsedField = fieldParts ?? field.field;
        const name = namePrefix !== undefined
            ? [namePrefix, ...(fieldParts ?? [field.field])]
            : parsedField;
        // Inside a Form.List the relative name [rowIndex, fieldName] is correct for
        // Form.Item registration, but form.getFieldValue / cross-field validators need
        // the absolute path [listName, rowIndex, fieldName] to find the value in the
        // form store. Build that here so validators and validateFields use the right path.
        // listName is normally a single Form.List name (string), but a DOUBLY-nested
        // list (a repeat field's subFields rendered inside an addRow group row) passes
        // the full absolute path array of its own list — spread it instead of wrapping.
        const absoluteName = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), ...(Array.isArray(name) ? name : [name])]
            : name;
        // Checkbox.Group (when options are configured) uses value=[]; single boolean checkbox uses checked=bool.
        const isCheckboxGroup = field.type === 'checkbox' && (
            (field.options && field.options.length > 0) ||
            (field.dataSource && field.dataSource !== 'static')
        );
        const valuePropName = (!isCheckboxGroup && field.type === 'checkbox') ? 'checked' : 'value';
        // Inline (combined) fields show no per-box label — the combined card prints a
        // single label and the boxes sit side by side (e.g. From / To / Currency).
        // hideLabel: the group renders one shared header row above all addRow rows
        // instead of repeating each field's label on every row (e.g. Languages Known).
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        // Sibling path prefix for evaluateShowIf/labelWhen: [listName, rowIndex]
        // in a Form.List, null for ordinary top-level fields.
        const showIfPrefix = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
            : null;
        // A condition may reference a sibling IN this row, or any top-level
        // field (e.g. contractType). Row prefix only applies when the referenced
        // key is one of this group's own fields; unknown membership (no
        // rowFieldKeys passed) keeps the historical row-first behavior.
        const prefixFor = (conditionKey) => (
            showIfPrefix && (!opts.rowFieldKeys || opts.rowFieldKeys.has(conditionKey))
                ? showIfPrefix
                : null
        );
        const fieldLabel = (opts.inline || opts.hideLabel)
            ? ''
            : (!isCheckboxGroup && field.type === 'checkbox')
                ? ''
                : <ConfiguredFieldLabel form={form} field={field} siblingPrefix={field.labelWhen?.field ? prefixFor(field.labelWhen.field) : showIfPrefix} locked={permissionLocked} />;
        const dependencies = getFieldDependencies(field, absoluteName, prefixFor);
        // Admin-set help text (field.tooltip, or the older infoEnabled/infoText
        // pair). It rides the label when there is one; a label-less field
        // (inline/combined box, or a group printing one shared header row) gets
        // it on the control instead — see fieldItem below — so EVERY control
        // type can carry a tooltip.
        const tooltipText = fieldTooltipText(field);
        const mdSpan = opts.inline ? 24 : ((field.type === 'file' && truthy(field.multiple)) || field.type === 'text-editor' ? 24 : (field.col ?? 12));

        // Config-driven default for the Add form: a top-level field (not inside a
        // Form.List) with a configured defaultValue mounts pre-selected. Any prefill
        // (initialRecord / quick-submit) runs through setFieldsValue, which overrides
        // initialValue — so this only affects a fresh Add with no incoming value.
        //
        // An autoValueRule for the acting user (e.g. Submit Source → "External"
        // for an employer-type user) outranks defaultValue: the field mounts
        // showing the value the gateway will enforce anyway. Display only — the
        // backend re-evaluates the same rules and has the final say.
        const autoValue = autoValueFor(field, actingIdentity);
        const initialValue = (namePrefix === undefined && field.type !== 'file'
            && autoValue.matched && autoValue.value !== undefined && autoValue.value !== '')
            ? autoValue.value
            : ((namePrefix === undefined && field.type !== 'file'
                && field.defaultValue !== undefined && field.defaultValue !== '')
                ? field.defaultValue : undefined);

        // AI Actions: admin-configured buttons (Generate/Parse-style) attached to
        // this field. "click" actions render as buttons beside the field; an
        // "upload" action (on a file field) fires automatically when a new file
        // is accepted, via the onAiUpload callback passed into FieldControl.
        const clickAiActions = (field.aiActions || []).filter((a) => a.trigger === 'click');
        const uploadAiAction = (field.aiActions || []).find((a) => a.trigger === 'upload');

        const fieldItem = (
            <Form.Item
                label={fieldLabel}
                name={name}
                extra={renderFieldHelp(field)}
                rules={getRules(field, form, absoluteName, isSearch, prefixFor, uniqueContext,
                    inProgressRulesFor(field, opts.groupNameForActions))}
                required={isFieldRequired(field, form, absoluteName)}
                dependencies={dependencies}
                valuePropName={valuePropName}
                initialValue={initialValue}
                getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                validateTrigger={['onBlur', 'onChange']}
            >
                <FieldControl
                    key={field.refreshToken != null ? `${field.field}-refresh-${field.refreshToken}` : undefined}
                    field={field}
                    form={form}
                    name={absoluteName}
                    rowFieldKeys={opts.rowFieldKeys}
                    moduleName={moduleName}
                    scopeValues={{ clientId: effectiveClientId }}
                    onAiUpload={uploadAiAction
                        ? (file) => aiActions.runAction(opts.groupNameForActions, field, uploadAiAction, file)
                        : undefined}
                    onDuplicateBlur={handleDuplicateBlur}
                    onRegionBlocked={handleRegionBlocked}
                    onRowOrderChange={handleRowOrder}
                    formDisabled={aiActions.busy}
                />
            </Form.Item>
        );

        // A label-less field (inline/combined box, or a group that prints one
        // shared header row) has nowhere to hang the label's info icon, so the
        // whole item becomes the tooltip target. The wrapping <span> is what
        // Tooltip attaches to — passing the Form.Item straight in would make
        // antd clone it for the ref, and Form.Item's clone is reserved for
        // injecting value/onChange into its control.
        const tooltippedItem = (tooltipText && !fieldLabel)
            ? <Tooltip title={tooltipText}><span className="v1-field-tooltip-wrap">{fieldItem}</span></Tooltip>
            : fieldItem;

        const fieldItemWithActions = clickAiActions.length > 0 ? (() => {
            const positioned = groupActionsByPosition(clickAiActions);
            const group = (position) => (
                <AiActionButtonGroup
                    key={position}
                    position={position}
                    actions={positioned[position]}
                    aiActions={aiActions}
                    groupName={opts.groupNameForActions}
                    field={field}
                />
            );
            return (
                <div className="v1-ai-action-wrap">
                    {group('top-left')}
                    {group('top-center')}
                    {group('top-right')}
                    {tooltippedItem}
                    {group('bottom-left')}
                    {group('bottom-center')}
                    {group('bottom-right')}
                </div>
            );
        })() : tooltippedItem;

        // The verify carrier fields (verifyResultField "isValid" / verifyTimestampField
        // "validatedAt") are show:0, so they are NOT rendered as Form.Items above —
        // which means the value VerifyFieldButton sets via setFieldValue lives on an
        // UNREGISTERED path and antd's onFinish drops it, so "verified" was never
        // persisted (and so an edit form had nothing to prefill). Registering them as
        // hidden Form.Items — under the SAME relative name (row-indexed inside an
        // addRow Form.List) — makes the set value survive submit AND lets an edit
        // prefill populate it, so VerifyFieldButton shows "Verified" for an already-
        // verified email. omitEmpty on the field config keeps an unverified row from
        // writing a null/false carrier.
        const carrierName = (key) => (namePrefix !== undefined ? [namePrefix, key] : key);
        const verifyCarriers = field.verifyAction ? (
            <>
                {field.verifyResultField && (
                    <Form.Item name={carrierName(field.verifyResultField)} hidden noStyle>
                        <Input />
                    </Form.Item>
                )}
                {field.verifyTimestampField && (
                    <Form.Item name={carrierName(field.verifyTimestampField)} hidden noStyle>
                        <Input />
                    </Form.Item>
                )}
            </>
        ) : null;

        const fieldItemWithVerify = field.verifyAction ? (
            <div className="v1-verify-field-row">
                {fieldItemWithActions}
                <VerifyFieldButton form={form} field={field} absoluteName={absoluteName} />
                {verifyCarriers}
            </div>
        ) : fieldItemWithActions;

        // Inside a combined block, each box's width follows its combineWidth weight
        // (flex-grow), so the pack can be sized by ratio (e.g. 2:1, 1:2:3). Unset → 1.
        const inlineStyle = opts.inline
            ? { flex: `${Number(field.combineWidth) > 0 ? Number(field.combineWidth) : 1} 1 0`, maxWidth: 'none' }
            : undefined;


        const autoFillWatcher = field.autoFillFromApi ? (
            <AutoFillFromApiWatcher
                key={`${field.field}-autoFillFromApi`}
                form={form}
                config={field.autoFillFromApi}
                watchPath={absoluteName}
            />
        ) : null;

        const prefillWatcher = field.prefillWhen?.length ? (
            <PrefillWhenWatcher
                key={`${field.field}-prefillWhen`}
                form={form}
                rules={field.prefillWhen}
                watchPaths={prefillWhenWatchPaths(field.prefillWhen, absoluteName, resolveScopedFieldPath, prefixFor)}
                targetPath={absoluteName}
                resetOnNoMatch={field.prefillWhenReset !== false}
            />
        ) : null;

        // A show:0 field only reaches here because it has prefillWhen (see
        // the visibleFields filter above) — it exists purely to derive and
        // hold a value via the watcher, never to be seen or directly edited.
        if (field.show === 0) {
            return (
                <Fragment key={field.field}>
                    {prefillWatcher}
                    {autoFillWatcher}
                    <Form.Item name={name} hidden noStyle>
                        <input type="hidden" />
                    </Form.Item>
                </Fragment>
            );
        }

        // colWhen: live grid width, so a field can shrink when a condition
        // reveals a sibling next to it. Inline (combined) cells are flex-sized
        // by combineWidth, so they keep their fixed span.
        const hasColWhen = !opts.inline && colWhenRules(field).length > 0;
        const spanFor = (getFieldValue) => (hasColWhen
            ? resolveColSpan(field, mdSpan, (rule) => getFieldValue(conditionFieldPath(rule, prefixFor(rule.field))))
            : mdSpan);

        if (hasShowIf(field.showIf) || hasColWhen) {
            return (
                <Fragment key={field.field}>
                    {prefillWatcher}
                    {autoFillWatcher}
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) =>
                            (!hasShowIf(field.showIf) || evaluateShowIf(field.showIf, getFieldValue, prefixFor))
                                ? <Col xs={24} md={spanFor(getFieldValue)} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItemWithVerify}</Col>
                                : null
                        }
                    </Form.Item>
                </Fragment>
            );
        }
        return (
            <Fragment key={field.field}>
                {prefillWatcher}
                {autoFillWatcher}
                <Col xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItemWithVerify}</Col>
            </Fragment>
        );
    };

    // renderAddRowField — field-level "addRow": repeats a SINGLE field's control
    // with the same add/remove + min/initial/max config the group-level addRow
    // uses (Form.List), but each list item IS the field's value directly (no
    // sub-key row object). Config keys mirror the group ones: field.minRows /
    // field.initialRows / field.maxRows / field.addLabel.
    //   • minRows    — delete is disabled once the list reaches this count.
    //   • initialRows— empty inputs shown on a fresh Add (falls back to minRows).
    //   • maxRows    — the "Add" button hides once the list reaches this count.
    // Required (field.req) is enforced at the LIST level (>=1 non-empty entry) —
    // per-item rules skip "required" so an empty-but-present item never blocks.
    // Each item renders through singleFieldOf(field) so a repeatable select/
    // multi field shows a SINGLE control per item, not a multi-select.
    // Only used for a top-level field (namePrefix === undefined) — a field with
    // addRow=true inside an addRow GROUP renders as a normal single-value field
    // (no nested repeats).
    const renderAddRowField = (field) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        // subFields → repeat a GROUP of fields per row (each row is an object of
        // the sub-fields, e.g. question + answer) instead of a single scalar.
        // Rows render through the same renderFields the group-level addRow uses.
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        // Sub-fields are always shown + editable (the admin exposes no show/edit
        // toggle for them). Force both so a sub-field never renders disabled: the
        // Go FormGroupField serializes show/edit as 0 when unset (no omitempty),
        // which editable() would otherwise read as "not editable".
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        // Prefill source (quick-submit / initialRecord) — the Form.List mounts
        // only once the form is ready, so this initialValue is correct at mount.
        const prefillSource = pickPrefillValue(initialRecord ?? prefillFlatRecord ?? {}, field);
        const initialValue = buildRepeatFieldInitial(field, prefillSource);

        return (
            <Col key={field.field} xs={24} md={24}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={fieldParts}
                        initialValue={initialValue}
                        rules={[{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = hasSubFields
                                        ? items.some((v) => hasMeaningfulValue(v))
                                        : items.some((v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} ${hasSubFields ? 'row' : 'value'}${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }]}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => (
                                    <div key={itemKey} className="v1-addrow-item">
                                        <Button
                                            type="text" danger icon={<DeleteOutlined />}
                                            onClick={() => remove(itemName)}
                                            disabled={items.length <= minRows}
                                            className="v1-addrow-delete-btn"
                                        />
                                        <div className="v1-addrow-fields">
                                            {hasSubFields ? (
                                                <Row gutter={[24, 0]} align="top">
                                                    {renderFields(subFieldsToRender, itemName, fieldParts[0], false, field.field)}
                                                </Row>
                                            ) : (
                                                <Form.Item
                                                    name={[itemName]}
                                                    noStyle
                                                    valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                    getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                                                    rules={getRules(itemField, form, [...fieldParts, itemName], isSearch)}
                                                >
                                                    <FieldControl
                                                        field={itemField}
                                                        form={form}
                                                        name={[...fieldParts, itemName]}
                                                        moduleName={moduleName}
                                                        scopeValues={{ clientId: effectiveClientId }}
                                                        formDisabled={aiActions.busy}
                                                    />
                                                </Form.Item>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    // renderNestedRepeatField — a repeat field (field.addRow) that lives INSIDE
    // an addRow group row. It nests its own Form.List under the current row so
    // every row owns an independent set of repeat values (the payload shape
    // group[rowIndex].repeatField[repeatIndex], e.g. educationDetails[0].subjects).
    // Also handles the field.subFields case (repeat a GROUP of sub-fields
    // together per row, e.g. question + answer) nested the same way — each item
    // renders the sub-fields through the same renderFields the top-level
    // subFields branch (renderAddRowField) uses, so a select/lookup sub-field
    // gets the exact same config-driven dropdown loading as any other field.
    // namePrefix is the outer row's index (from the group Form.List), listName
    // is the group's Form.List name (string) or, for a doubly-nested case, its
    // absolute path array. The nested list's `name` is relative to that list
    // context ([rowIndex, ...fieldParts]); item controls use the absolute path
    // for validators/FieldControl.
    const renderNestedRepeatField = (field, namePrefix, listName) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        // Relative name (group-list context prefixes the group name); the parent
        // row's initialValue already carries any stored values for this key, so a
        // fresh row falls back to config-driven empty inputs while an edited row
        // inherits its stored values (antd ignores this initialValue when the
        // store already holds a value at this path).
        const listNameRel = [namePrefix, ...fieldParts];
        const nestedAbsoluteListName = [...(Array.isArray(listName) ? listName : [listName]), namePrefix, ...fieldParts];
        const initialValue = buildRepeatFieldInitial(field, undefined);
        return (
            <Col key={`${listName}-${namePrefix}-${field.field}`} xs={24} md={hasSubFields ? 24 : (field.col ?? 24)}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={listNameRel}
                        initialValue={initialValue}
                        rules={hasSubFields ? [{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = items.some((v) => hasMeaningfulValue(v));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }] : []}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => {
                                    const absoluteName = [...nestedAbsoluteListName, itemName];
                                    return (
                                        <div key={itemKey} className="v1-addrow-item">
                                            <Button
                                                type="text" danger icon={<DeleteOutlined />}
                                                onClick={() => remove(itemName)}
                                                disabled={items.length <= minRows}
                                                className="v1-addrow-delete-btn"
                                            />
                                            <div className="v1-addrow-fields">
                                                {hasSubFields ? (
                                                    <Row gutter={[24, 0]} align="top">
                                                        {renderFields(subFieldsToRender, itemName, nestedAbsoluteListName, false, field.field)}
                                                    </Row>
                                                ) : (
                                                    <Form.Item
                                                        name={[itemName]}
                                                        noStyle
                                                        valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                        getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                                                        rules={getRules(itemField, form, absoluteName, isSearch)}
                                                    >
                                                        <FieldControl
                                                            field={itemField}
                                                            form={form}
                                                            name={absoluteName}
                                                            moduleName={moduleName}
                                                            scopeValues={{ clientId: effectiveClientId }}
                                                            formDisabled={aiActions.busy}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    const renderFields = (fields, namePrefix, listName, hideLabel = false, groupNameForActions) => {
        const used = new Set();
        const out = [];
        // Keys of THIS group's own fields — conditions (showIf/labelWhen/
        // prefillWhen) referencing a field OUTSIDE the group (e.g. the top-level
        // contractType from inside the addRow employer group) must resolve
        // against the top-level form, not the row.
        const rowFieldKeys = new Set(fields.map((f) => f.field).filter(Boolean));
        // Hidden (show:0) fields stay in `fields` so buildPayload can see them
        // (see normalizeGroups) — they just never get a Form.Item here.
        const visibleFields = fields.filter((f) => (
            f.show === undefined || truthy(f.show) || (f.show === 0 && Boolean(f.prefillWhen?.length))
        ));
        visibleFields.forEach((field) => {
            if (truthy(field.addRow) && namePrefix === undefined) {
                out.push(renderAddRowField(field));
                return;
            }
            // A repeat field INSIDE an addRow group row (namePrefix defined) gets
            // its own nested Form.List so each row keeps independent repeat values
            // (educationDetails[rowIndex].subjects[]) — including a subFields
            // repeat-GROUP (e.g. question+answer pairs per education row).
            if (truthy(field.addRow) && namePrefix !== undefined) {
                out.push(renderNestedRepeatField(field, namePrefix, listName));
                return;
            }
            const combineKey = field.combineKey?.trim?.();
            if (!combineKey) {
                out.push(renderField(field, namePrefix, listName, { hideLabel, groupNameForActions, rowFieldKeys }));
                return;
            }
            const packKey = `${listName ?? 'root'}:${namePrefix ?? 'root'}:${combineKey}`;
            if (used.has(packKey)) return;
            const pack = visibleFields.filter((f) => f.combineKey === combineKey);
            pack.forEach((f) => used.add(`${listName ?? 'root'}:${namePrefix ?? 'root'}:${f.combineKey}`));

            const labelField = pack.find((f) => f.combineLabel) ?? field;
            const siblingPrefix = (listName !== undefined && namePrefix !== undefined)
                ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
                : null;
            const label = <ConfiguredFieldLabel form={form} field={labelField} siblingPrefix={siblingPrefix} combined />;
            const toggleTarget = pack.find((f) => f.combineToggleTarget)?.combineToggleTarget;
            const toggleLabel = pack.find((f) => f.combineToggleLabel)?.combineToggleLabel ?? 'Show range';
            const toggleStateKey = packKey;
            const wrapPack = pack.some((f) => truthy(f.combineWrap));
            const configuredSpan = Number(pack.find((f) => [12, 24].includes(Number(f.combineSpan)))?.combineSpan);
            const targetField = toggleTarget ? pack.find((f) => fieldKey(f) === toggleTarget) : null;
            const targetParts = targetField?.field?.includes?.('.') ? targetField.field.split('.') : [targetField?.field].filter(Boolean);
            const targetName = targetField
                ? ((listName !== undefined && namePrefix !== undefined)
                    ? [listName, namePrefix, ...targetParts]
                    : (targetParts.length > 1 ? targetParts : targetField.field))
                : null;

            // Layout mirrors the legacy combined field: one label, the boxes inline on
            // a single row, and the range toggle BELOW. Toggle off → only the primary
            // (target hidden); toggle on → the target box appears alongside. The wider
            // packs (with currency/unit) take a full row; a simple From/To pair is half.
            const primaryReq = pack.some((f) => isFieldRequired(f));
            const comboSpan = [12, 24].includes(configuredSpan)
                ? configuredSpan
                : (pack.length >= 3 ? 24 : 12);
            // A combined pack prints ONE shared label above its boxes, and that
            // label lives outside the per-field showIf gates. When every member
            // is hidden by its condition the boxes disappear but the labelled
            // shell stayed — e.g. an empty "Contact Number" on a W2 employer.
            // Gate the whole pack on "is any member still visible".
            const packPrefixFor = (conditionKey) => (
                siblingPrefix && (!rowFieldKeys || rowFieldKeys.has(conditionKey)) ? siblingPrefix : null
            );
            const packHasShowIf = pack.some((f) => hasShowIf(f.showIf));
            const anyPackMemberVisible = (getFieldValue) => pack.some(
                (f) => !hasShowIf(f.showIf) || evaluateShowIf(f.showIf, getFieldValue, packPrefixFor),
            );
            const packCol = (
                <Col key={packKey} xs={24} md={comboSpan}>
                    <Form.Item label={label} required={primaryReq} colon={false} className="v1-combine-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const hasTargetValue = targetName ? !empty(getFieldValue(targetName)) : false;
                                const checked = combineToggles[toggleStateKey] ?? hasTargetValue;
                                const visiblePack = toggleTarget && !checked
                                    ? pack.filter((f) => fieldKey(f) !== toggleTarget)
                                    : pack;
                                return (
                                    <>
                                        <Row
                                            gutter={[8, 0]}
                                            align="top"
                                            wrap={wrapPack}
                                            className={`v1-combine-row${wrapPack ? ' v1-combine-row-wrap' : ''}`}
                                        >
                                            {/* Combined-field mandatory cascade: if ANY field in a
                                                combined pack is required, every VISIBLE field in the
                                                pack is required too (e.g. Notice Period requires its
                                                Unit). Applied to visiblePack so a hidden range "To"
                                                stays optional until its toggle is on. */}
                                            {visiblePack.map((f) => renderField(primaryReq ? { ...f, req: 1 } : f, namePrefix, listName, { inline: true }))}
                                        </Row>
                                        {toggleTarget && (
                                            <div className="v1-combine-range">
                                                <Switch
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(next) => {
                                                        if (!next && targetName) {
                                                            form.setFieldValue(targetName, undefined);
                                                            form.validateFields([targetName]).catch(() => { });
                                                        }
                                                        setCombineToggles((prev) => ({ ...prev, [toggleStateKey]: next }));
                                                    }}
                                                />
                                                <Text type="secondary" className="v1-combine-toggle-label">{toggleLabel}</Text>
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </Col>
            );
            out.push(packHasShowIf ? (
                <Form.Item key={packKey} shouldUpdate noStyle>
                    {({ getFieldValue }) => (anyPackMemberVisible(getFieldValue) ? packCol : null)}
                </Form.Item>
            ) : packCol);
        });
        return out;
    };

    // Derive fields from search results for ListView (same logic as JobsListPage)
    const searchFields = useMemo(() => {
        if (searchResults.fields?.length) return searchResults.fields;
        const firstRow = searchResults.rows[0];
        if (!firstRow) return [];

        return Object.keys(firstRow)
            .filter((key) => !key.startsWith('_') && typeof firstRow[key] !== 'object')
            .map((key) => ({
                value: key,
                field: key,
                label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
                isVisible: true,
            }));
    }, [searchResults.fields, searchResults.rows]);

    // Determine view mode based on source type: show list view for external sources,
    // card view for internal sources
    const getViewModeFromRecords = (records) => {
        if (!Array.isArray(records) || records.length === 0) return 'card';

        const hasExternalSource = records.some((record) => {
            const sourceFields = [
                record?.sourceType,
                record?.profileSource,
                record?.selectedSource,
                record?.customFields?.sourceType,
                record?.customFields?.profileSource,
            ];

            return sourceFields.some((value) => {
                if (value === null || value === undefined) return false;
                const normalized = String(value).trim().toLowerCase();
                if (!normalized) return false;
                return !['all', 'internal'].includes(normalized);
            });
        });

        return hasExternalSource ? 'list' : 'card';
    };

    const defaultViewMode = useMemo(() => {
        return getViewModeFromRecords(searchResults.rows);
    }, [searchResults.rows]);

    const searchFieldsWithActions = useMemo(() => {
        const sourceActions = getAvailableSourceActions({
            rows: searchResults.rows,
            selectedSource,
        });
        if (!sourceActions.length) return searchFields;

        const actionFields = new Set(sourceActions.map((action) => action.field));
        const fieldsWithoutActions = searchFields.filter(
            (field) => field.cardRole !== 'action' && !actionFields.has(field.value),
        );
        return [
            ...fieldsWithoutActions,
            ...sourceActions.map((action) => ({
                value: action.field,
                field: action.field,
                label: action.label ?? 'Action',
                cardRole: 'action',
                isVisible: true,
                tableHidden: true,
                sorter: false,
                render: (_value, record) => {
                    if (!sourceActionAppliesToRecord(action, record, selectedSource)) return null;
                    const actionKey = getSourceActionInstanceKey(action, record);
                    const state = sourceActionState[actionKey] ?? {};
                    const missingActionId = !actionKey;
                    const disabled = missingActionId || state.completed;

                    return (
                        <Button
                            type="primary"
                            size="small"
                            loading={state.loading}
                            disabled={disabled}
                            onClick={() => handleSourceAction(action, record)}
                        >
                            {state.completed ? action.completedLabel : action.buttonLabel}
                        </Button>
                    );
                },
            })),
        ];
    }, [handleSourceAction, searchFields, searchResults.rows, selectedSource, sourceActionState]);

    // Merge page-level context (e.g. { jobId }) into every search-result row
    // action's popupState, so a popup it opens (via ListView's action.route
    // handling) receives that context too — see the searchActionContext prop.
    const searchResultActions = useMemo(() => {
        if (!searchActionContext) return searchResults.actions;
        const mergeContext = (action) => ({
            ...action,
            popupState: { ...searchActionContext, ...action.popupState },
            ...(action.children ? { children: action.children.map(mergeContext) } : {}),
        });
        return (searchResults.actions ?? []).map(mergeContext);
    }, [searchResults.actions, searchActionContext]);

    // renderGroupBody builds a group's inner controls (the addRow Form.List or
    // the flat field Row) WITHOUT its Card chrome, so a group flagged
    // `mergeInto: "<hostGroupName>"` can render inside another group's card
    // (e.g. Rate Details inside Employer) while keeping its own payload,
    // appendHistory and showIf semantics completely unchanged — the merge is
    // presentation only.
    const renderGroupBody = (group) => {
        const groupName = group.name ?? group.id ?? '';
        // group.linkGroup: when this group is linked to others, the LEADER owns
        // the Add button + minRows/requireOne validator for the whole set; every
        // member (leader + followers) still gets its own Form.List/Card/rows, and
        // add/remove always go through every member via listControlsRef so row
        // counts stay equal by construction. Unlinked groups (linkedSet === null,
        // i.e. every group that predates this feature) fall through leaderGroup
        // === group, so minRows/rules/initialValue below compute exactly as before.
        const linkedSet = findLinkedSet(linkedGroupSets, groupName);
        const isFollower = Boolean(linkedSet) && linkedSet.leaderName !== groupName;
        const leaderGroup = linkedSet
            ? (groups.find((g) => g.name === linkedSet.leaderName) ?? group)
            : group;
        const minRows = Math.max(0, Number(leaderGroup.minRows ?? 0) || 0);
        const maxRows = Math.max(0, Number(leaderGroup.maxRows ?? 0) || 0);
        const linkedMemberNames = linkedSet ? linkedSet.memberNames : [groupName];
        const addLinkedRow = () => {
            // New rows start from the group's admin-configured defaults (same
            // seed the initial rows get), not empty.
            linkedMemberNames.forEach((name) => {
                const member = groups.find((g) => g.name === name) ?? group;
                listControlsRef.current[name]?.add(rowDefaultsFor(member));
            });
        };
        const removeLinkedRow = (rowName) => {
            linkedMemberNames.forEach((name) => listControlsRef.current[name]?.remove(rowName));
        };
        const body = group.addRow ? (
            <Form.List
                name={groupName}
                initialValue={addRowInitials[groupName] ?? []}
                rules={isFollower ? [] : [
                    {
                        validator: async (_, rows = []) => {
                            // A group switched OFF via its own toggle (e.g. "Fresher" hiding
                            // Work Experience) is not rendered at all — don't force rows on it.
                            const switchedOn = truthy(group.toggleEnabled) && group.toggleField
                                && Boolean(form.getFieldValue(group.toggleField));
                            if (switchedOn) return;
                            if (minRows > 0 && rows.length < minRows) {
                                throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                            }
                            if (truthy(group.requireOne)) {
                                const invalid = rows.some((row) => !hasMeaningfulValue(row));
                                if (invalid) throw new Error('Fill at least one field in each added row.');
                            }
                        },
                    },
                ]}
            >
                {(rows, { add, remove, move }, { errors }) => {
                    // `move` joins the registry so the newest-first prompt can
                    // reorder a row the user just marked as ongoing. Form.List's
                    // own move preserves every row's values; rebuilding the
                    // array by hand would not.
                    listControlsRef.current[groupName] = { add, remove, move };
                    return (
                        <div className="v1-addrow-wrapper">
                            <div className="v1-addrow-topbar">
                                {!isFollower ? (
                                    (!maxRows || rows.length < maxRows) && (
                                        <Button
                                            type="link"
                                            icon={<PlusOutlined />}
                                            onClick={addLinkedRow}
                                            className="v1-addrow-add-btn"
                                        >
                                            {group.addLabel ?? `Add ${group.label ?? 'Row'}`}
                                        </Button>
                                    )
                                ) : (
                                    <Text type="secondary" className="v1-addrow-linked-hint">
                                        Rows added via {leaderGroup.label ?? leaderGroup.name}
                                    </Text>
                                )}
                            </div>

                            {truthy(group.sharedRowLabels) && rows.length > 0 && (
                                <Row gutter={[24, 0]} className="v1-addrow-header">
                                    {group.fields.filter((f) => f.show === undefined || truthy(f.show)).map((field) => (
                                        <Col key={fieldKey(field)} md={field.col ?? 12} className="v1-addrow-header-label">
                                            {field.label}
                                        </Col>
                                    ))}
                                </Row>
                            )}

                            {rows.map(({ key: rowKey, name }) => (
                                <div key={rowKey} className="v1-addrow-item">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeLinkedRow(name)}
                                        disabled={rows.length <= minRows}
                                        className="v1-addrow-delete-btn"
                                    />

                                    <div className="v1-addrow-fields">
                                        <Row gutter={[24, 0]} align="top">
                                            {renderFields(group.fields, name, groupName, truthy(group.sharedRowLabels), groupName)}
                                        </Row>
                                    </div>
                                </div>
                            ))}
                            <Form.ErrorList errors={errors} />
                        </div>
                    );
                }}
            </Form.List>
        ) : (
            <Row gutter={[24, 0]}>{renderFields(group.fields, undefined, undefined, false, groupName)}</Row>
        );

        return body;
    };

    // Groups rendered INSIDE another group's card (group.mergeInto). A child
    // keeps its own showIf gate and gets a light section title; everything else
    // (payload, validation, Form.List names) is untouched.
    const mergedChildrenOf = (group) => groups.filter(
        (g) => g !== group && g.mergeInto && String(g.mergeInto) === String(group.name),
    );
    const isMergedChild = (group) => Boolean(
        group.mergeInto && groups.some((g) => g !== group && String(g.name) === String(group.mergeInto)),
    );

    // A merged child renders BEFORE the host's own fields when it declares
    // `mergePosition: "top"`. Needed whenever the child holds the field the
    // host's fields are conditioned on — Contract Type gates every Employer
    // field, so rendering it underneath them reads backwards and invites the
    // user to fill a disabled card first.
    const mergedAt = (group, position) => mergedChildrenOf(group).filter(
        (child) => (String(child.mergePosition ?? 'bottom') === position),
    );


    const renderGroup = (group, index) => {
        const key = group.name ?? group.id ?? String(index);
        const body = renderGroupBody(group);
        const mergedTop = mergedAt(group, 'top');
        const mergedChildren = mergedAt(group, 'bottom');

        // One renderer for both slots, so a merged child behaves identically
        // whichever side of the host body it sits on.
        const renderMergedChild = (child) => {
            const section = (
                <div key={child.name} className="v1-merged-group">
                    <div className="v1-merged-group-title">{child.label ?? child.name}</div>
                    {renderGroupBody(child)}
                </div>
            );
            const gated = child.showIf?.field
                ? <GroupShowIfGate key={child.name} form={form} showIf={child.showIf} render={() => section} />
                : section;
            // Its OWN disabled state, not the host's — see GroupDisabledProvider.
            return (
                <GroupDisabledProvider key={child.name} form={form} group={child} inheritParent={false}>
                    {gated}
                </GroupDisabledProvider>
            );
        };

        return (
            <Card
                key={key}
                className="v1-card v1-edit-card"
                title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={isSearch ? (searchFormLabel || group.label) : (group.label ?? group.name ?? 'Details')} />}
                extra={(
                    <Space size={8} align="center" className="v1-group-actions">
                        <GroupInfoMessage group={group} />
                        <GroupToggleControl group={group} />
                        <Button
                            type="text"
                            size="small"
                            icon={collapsed[key] ? <DownOutlined /> : <UpOutlined />}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggle(key);
                            }}
                        />
                    </Space>
                )}
            >
                <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                        const switchedOn = truthy(group.toggleEnabled) && group.toggleField && Boolean(getFieldValue(group.toggleField));
                        return (
                            <div style={{ display: collapsed[key] || switchedOn ? 'none' : 'block' }}>
                                {mergedTop.map(renderMergedChild)}
                                {body}
                                {mergedChildren.map(renderMergedChild)}
                            </div>
                        );
                    }}
                </Form.Item>
            </Card>
        );
    };

    return (
        <main>
            {breadcrumbItems.length > 0 && <AppBreadcrumb items={breadcrumbItems} />}
            <div className="v1-edit-page">
                {hasClientScope && !presetClientId && !linkedClientId && (
                    <Card className="v1-card v1-edit-card v1-client-gate-card">
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <SectionTitle icon={<UserOutlined />} title="Select Client" />
                            {clientGateMessage && <Text type="secondary">{clientGateMessage}</Text>}
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={14}>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select client"
                                        style={{ width: '100%' }}
                                        value={effectiveClientId || undefined}
                                        optionFilterProp="label"
                                        options={clientOptions.map((c) => ({
                                            value: c._id ?? c.id,
                                            label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                                        })).filter((c) => c.value)}
                                        onChange={handleClientGateChange}
                                        dropdownRender={clientGateQuick.footer
                                            ? (menu) => (<>{menu}{clientGateQuick.footer}</>)
                                            : undefined}
                                    />
                                    {clientGateQuick.modal}
                                </Col>
                            </Row>
                        </Space>
                    </Card>
                )}
                <div className="v1-page-inner">
                    {/* Async work gets its OWN viewport-centred overlay.
                        The wrapping Spin below centres its indicator inside the
                        element it wraps — on a form this long that lands
                        somewhere in the middle of the page, which after any
                        scrolling is off screen entirely. Reading a résumé takes
                        seconds, so the user is left looking at a frozen form
                        with no visible sign that anything is happening.
                        `fullscreen` pins it to the centre of the VIEWPORT with
                        a scrim, so it is where the user is looking whatever
                        they have scrolled to. */}
                    <Spin
                        fullscreen
                        spinning={aiActions.busy || duplicates.checking}
                        size="large"
                        tip={
                            aiActions.busy
                                ? (aiActions.loadingText || 'Reading the file…')
                                : 'Checking whether we already have this person…'
                        }
                    />
                    <Spin
                        spinning={!isFormReady && !waitingForClientScope}
                        description="Loading form fields…"
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            colon={false}
                            className="v1-form"
                            // Disabled during the duplicate check too — it can
                            // end by navigating away, and letting someone keep
                            // typing into a form that is about to be abandoned
                            // loses whatever they add in those seconds.
                            disabled={aiActions.busy || duplicates.checking}
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="v1-required-mark"> *</span>}
                                </>
                            )}
                            onFinish={submit}
                            onFinishFailed={(info) => {
                                // Expand collapsed sections first — a hidden error
                                // field can't be scrolled to or focused.
                                setCollapsed({});
                                scrollToFirstFormError(info, form);
                            }}
                        >
                            {isFormReady && groups.filter((g) => !isMergedChild(g)).map((group, index) => (
                                <GroupDisabledProvider key={group.name ?? `grp-${index}`} form={form} group={group}>
                                    {group.showIf?.field
                                        ? <GroupShowIfGate form={form} showIf={group.showIf} render={() => renderGroup(group, index)} />
                                        : renderGroup(group, index)}
                                </GroupDisabledProvider>
                            ))}
                            {isFormReady && !error && groups.length === 0 && (
                                <Alert type="info" showIcon message={isSearch ? "No search configuration found." : "No form fields are configured."} />
                            )}
                            {isFormReady && isSearch && (
                                <div className="v1-search-form-actions">
                                    <Space size={12}>
                                        <Button onClick={handleReset} disabled={!groups.length}>
                                            Reset
                                        </Button>
                                        <Button onClick={() => goBackOrTo(navigate, { cancelPath })}>
                                            Back
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            disabled={submitLoading || !groups.length || searchDisabled}
                                            loading={submitLoading}
                                        >
                                            {submitText}
                                        </Button>
                                    </Space>
                                </div>
                            )}
                        </Form>
                    </Spin>
                </div>

                {!isSearch && (
                    <div className={embedded ? 'v1-footer v1-footer--embedded' : 'v1-footer'}>
                        <Text type="secondary">All Unsaved Changes will be lost if you cancel and reload.</Text>
                        <Space size={12}>
                            <Button onClick={() => (embedded && onCancel
                                ? onCancel()
                                : goBackOrTo(navigate, { cancelPath }))}>Cancel</Button>
                            {formActions.map((action, index) => (
                                <FormActionButton
                                    key={`${action.label ?? 'action'}-${index}`}
                                    form={form}
                                    groups={groups}
                                    action={action}
                                    disabled={!isFormReady || submitLoading || uniquePending > 0 || !groups.length}
                                    loading={submitLoading}
                                    onFire={() => {
                                        pendingAfterSubmitRef.current = action.afterSubmit ?? { mode: 'stay' };
                                        // Opt-in only — an action can force specific field
                                        // values right before submitting (e.g. a distinct
                                        // "Schedule" button implying scheduleForLater=true
                                        // without requiring the checkbox to be checked).
                                        if (action.setValuesBeforeSubmit) {
                                            form.setFieldsValue(action.setValuesBeforeSubmit);
                                        }
                                        form.submit();
                                    }}
                                />
                            ))}
                            <ValidationGatedSubmitButton
                                form={form}
                                groups={groups}
                                onClick={() => { pendingAfterSubmitRef.current = null; form.submit(); }}
                                // A pending uniqueness check must resolve before
                                // the record can be created.
                                disabled={!isFormReady || submitLoading || uniquePending > 0 || !groups.length}
                                loading={submitLoading}
                            >
                                {submitText}
                            </ValidationGatedSubmitButton>
                        </Space>
                    </div>
                )}

                {/* Search results ListView — rendered inside AddFormV1 when in search mode */}
                {isSearch && searchResults.searched && (
                    <ListView
                        moduleName={moduleName}
                        breadcrumbItems={[]}
                        dataSource={searchResults.rows}
                        fields={searchFieldsWithActions}
                        total={searchResults.total}
                        loading={searchResults.loading}
                        current={searchPagination.current}
                        pageSize={searchPagination.pageSize}
                        showViewToggle
                        activeView={viewMode}
                        onViewChange={setViewMode}
                        onPageChange={handleSearchPageChange}
                        onPageSizeChange={handleSearchPageSizeChange}
                        showSearch={false}
                        tabs={tabs}
                        selectedSource={selectedSource}
                        onTabChange={handleTabChange}
                        actions={searchResultActions}
                        actionRules={searchResults.actionRules}
                    />
                )}
            </div>
        </main>
    );
});
